import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import { Box, Typography, IconButton, Button, Tooltip, Chip, CircularProgress, Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { usePlannerTheme } from '../../theme/plannerTheme';
import { useSelector, useDispatch } from 'react-redux';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import { addToast } from '../../store/slices/notificationsSlice';
import ConfirmDialog from '../common/ConfirmDialog';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import TrafficIcon from '@material-ui/icons/Traffic';
import LayersIcon from '@material-ui/icons/Layers';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import TimelineIcon from '@material-ui/icons/Timeline';
import HomeIcon from '@material-ui/icons/Home';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { GoogleMap, useJsApiLoader, Polyline, Marker, TrafficLayer, InfoWindow } from '@react-google-maps/api';
import CompassRose from '../common/CompassRose';
import RoutePlaybackDialog from './RoutePlaybackDialog';
import { fetchIncidentsForViewport, INCIDENT_TYPE_MAP } from '../../utils/hereApi';
import { fetchIncidentsTomTomBbox } from '../../utils/tomtomApi';
import DriverFirstPersonView from './DriverFirstPersonView';
import LiveTelemetryCard from './LiveTelemetryCard';
import ChatDriverDialog from '../Orders/ChatDriverDialog';
import { selectUI } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import { getSocket } from '../../utils/websocket';
import { openLiveWindow } from '../../utils/liveWindow';
import { buildVehicleMarkerIcon, onVehicleIconLoaded, vehicleIconCategory, getVehicleIconDataUrl } from '../../utils/vehicleIcons';
import { otLabel, otTooltip } from '../../utils/otLabel';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 39.5, lng: -8.0 };
// O trajecto actualiza-se por push (WS): cada fix da app dispara
// `driver:position`, e o GPS Traccar chega no stream partilhado
// `vehicles:update` (1 poll server-side a cada 5s, seja qual for o nº
// de janelas Live abertas). O fetch a /live-tracking serve só de
// reconciliação de fundo — repõe o trajecto autoritativo e a fonte
// activa caso algum evento se perca. Daí 2 min, não 30s.
// Auto-refresh do /live-tracking — antes era 2 min, mas com provider
// Traccar o stream `vehicles:update` pode trazer fixTimes repetidos
// (device parado) e a dedupe bailava → marker congelado entre fixes.
// Com 15 s temos uma rede de segurança decente sem martelar a API.
const TRAIL_REFRESH_MS = 15000;
const ROUTE_REFRESH_MS = 60000;

const MAP_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false,
  gestureHandling: 'greedy',
};

function fmtDuration(sec) {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}min`;
}

function fmtTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}

// ─── Deviation detection ────────────────────────────────────────────────
// Threshold tuning — see notes in DEVIATION_CONFIG. Modern fleet apps
// (Waze, Google Maps Drive) reroute on traffic, accidents or ETA gain,
// so deviations are EXPECTED, not exceptional. The point isn't to flag
// the driver as "wrong" — it's to give the operator visibility and a
// permanent record of when the route diverged from plan.
const DEVIATION_CONFIG = {
  enterMeters: 300,    // must be at least this far off the line to start counting
  exitMeters: 150,     // must come back within this radius to clear
  enterSeconds: 90,    // must stay off for this long before raising the alert
  // Throttle for POSTs — once a deviation is reported, subsequent
  // worsening (longer duration / larger distance) is sent at most every
  // 60s to avoid spamming the backend.
  reportThrottleMs: 60000,
  // Threshold APENAS para o coloring visual do trail (verde/vermelho)
  // no mapa. Mais apertado que `enterMeters` porque o objectivo aqui é
  // diferente: o alerta dá ruído se disparar com 80 m (carros mudam de
  // faixa, contornam rotundas), mas no mapa o operador precisa de
  // SEMPRE saber, à vista, onde o caminho real não seguiu o planeado.
  // 80 m é o sweet-spot: maior que erros típicos de GPS (±5–25 m em
  // cidade) e que larguras de avenida (≈40 m), mas pequeno o suficiente
  // para apanhar atalhos por ruas paralelas.
  visualOffMeters: 80,
};

// Haversine (m) — same formula used elsewhere; inlined to avoid import
// cycles with services/location.js (which is mobile-only).
function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Remove "spikes" — fixes GPS disparatados que saltam para longe e
// voltam, criando linhas rectas compridas no mapa. Um ponto é spike
// quando está muito longe do anterior E do seguinte, mas esses dois
// estão perto um do outro (o ponto "salta para fora e volta").
const SPIKE_M = 800; // salto considerado implausível
function despikeTrail(pts) {
  if (!Array.isArray(pts) || pts.length < 3) return pts || [];
  const out = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = out[out.length - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const dPrev = haversineM(prev.lat, prev.lng, cur.lat, cur.lng);
    const dNext = haversineM(cur.lat, cur.lng, next.lat, next.lng);
    const dSkip = haversineM(prev.lat, prev.lng, next.lat, next.lng);
    // Spike: longe de ambos os vizinhos, mas vizinhos próximos entre si.
    if (dPrev > SPIKE_M && dNext > SPIKE_M && dSkip < SPIKE_M) continue;
    out.push(cur);
  }
  // Último ponto — descarta se for um salto isolado face ao anterior.
  const last = pts[pts.length - 1];
  const beforeLast = out[out.length - 1];
  if (haversineM(beforeLast.lat, beforeLast.lng, last.lat, last.lng) <= SPIKE_M * 3) {
    out.push(last);
  }
  return out;
}

// Distance from `point` to the closest segment of `polyline`. Uses planar
// approximation (lat/lng deltas treated as flat) with a cosine correction
// — accurate enough at the scales we care about (city/regional routes,
// not transcontinental). Returns metres.
function pointToPolylineMeters(point, polyline) {
  if (!Array.isArray(polyline) || polyline.length === 0) return Infinity;
  if (polyline.length === 1) return haversineM(point.lat, point.lng, polyline[0].lat, polyline[0].lng);
  // Compute everything in metric using a local equirectangular projection
  // centred on the point — error <0.5m for distances under ~10 km.
  const cosLat = Math.cos(point.lat * Math.PI / 180);
  const px = 0;
  const py = 0;
  const M_PER_DEG = 111320; // metres per degree of latitude
  function project(p) {
    return {
      x: (p.lng - point.lng) * M_PER_DEG * cosLat,
      y: (p.lat - point.lat) * M_PER_DEG,
    };
  }
  let best = Infinity;
  let prev = project(polyline[0]);
  for (let i = 1; i < polyline.length; i++) {
    const cur = project(polyline[i]);
    // Distance from origin (px, py) to segment (prev, cur).
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    const len2 = dx * dx + dy * dy;
    let t = 0;
    if (len2 > 0) {
      t = ((px - prev.x) * dx + (py - prev.y) * dy) / len2;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const cx = prev.x + t * dx;
    const cy = prev.y + t * dy;
    const d = Math.sqrt(cx * cx + cy * cy);
    if (d < best) best = d;
    prev = cur;
  }
  return best;
}

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes devPulse': {
      '0%':   { boxShadow: '0 0 0 0 rgba(239,68,68,0.55)' },
      '70%':  { boxShadow: '0 0 0 10px rgba(239,68,68,0)' },
      '100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0)' },
    },
    // Pulsar do botão de chat quando há mensagens por ler.
    '@keyframes chatPulse': {
      '0%':   { boxShadow: '0 0 0 0 rgba(239,68,68,0.6)' },
      '70%':  { boxShadow: '0 0 0 9px rgba(239,68,68,0)' },
      '100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0)' },
    },
    // ── Temas Claro/Escuro do ecrã LIVE (seguem o tema do avatar) ──────
    // Tokens --live-* por data-live-theme na raiz; cascateiam p/ os painéis
    // flutuantes (não são portais). Cabeçalho modernizado (navy limpo em vez
    // do cinzento datado).
    '[data-live-theme="light"]': {
      '--live-page': '#eef2f6',
      '--live-card': '#ffffff',
      '--live-card-2': '#f8fafc',
      '--live-border': '#e2e8f0',
      '--live-border-soft': '#eef2f7',
      '--live-strong': '#0f172a',
      '--live-text': '#334155',
      '--live-muted': '#64748b',
      '--live-muted2': '#94a3b8',
      '--live-shadow': '0 10px 30px rgba(15,23,42,0.16)',
      '--live-shadow-sm': '0 4px 14px rgba(15,23,42,0.14)',
      '--live-header': 'linear-gradient(120deg, #182538 0%, #26354b 55%, #334155 100%)',
      '--live-header-border': 'rgba(255,255,255,0.14)',
      '--live-panel-head': 'linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%)',
    },
    '[data-live-theme="dark"]': {
      '--live-page': '#0b1522',
      '--live-card': '#122133',
      '--live-card-2': '#0e1b2b',
      '--live-border': 'rgba(110,193,228,0.16)',
      '--live-border-soft': 'rgba(255,255,255,0.06)',
      '--live-strong': '#eaf3fb',
      '--live-text': '#c4d3e3',
      '--live-muted': '#9fb3c8',
      '--live-muted2': '#7e95ac',
      '--live-shadow': '0 12px 34px rgba(0,0,0,0.55)',
      '--live-shadow-sm': '0 4px 16px rgba(0,0,0,0.5)',
      '--live-header': 'linear-gradient(120deg, #0a1322 0%, #0f2033 60%, #14263b 100%)',
      '--live-header-border': 'rgba(110,193,228,0.18)',
      '--live-panel-head': 'linear-gradient(180deg, #0e1b2b 0%, #0b1522 100%)',
    },
  },
  root: {
    width: '100%', height: '100%',
    display: 'flex', flexDirection: 'column',
    background: 'var(--live-page, #f1f5f9)', overflow: 'hidden',
    transition: 'background 0.25s ease',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '9px 14px', margin: 8, borderRadius: 16,
    background: 'var(--live-header, linear-gradient(120deg, #64748b 0%, #7c8ba1 52%, #94a3b8 100%))',
    color: '#fff', flexShrink: 0,
    border: '1px solid var(--live-header-border, rgba(255,255,255,0.20))',
    boxShadow: 'var(--live-shadow, 0 8px 26px rgba(15,23,42,0.22))',
  },
  // Botões do cabeçalho — chip translúcido arredondado (mais estilizado).
  backBtn: {
    color: '#fff', borderRadius: 10,
    background: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.18)',
    '&:hover': { background: 'rgba(255,255,255,0.30)' },
  },
  headerTitle: { fontSize: '0.95rem', fontWeight: 800, letterSpacing: '-0.01em' },
  headerMeta: { fontSize: '0.68rem', color: 'rgba(255,255,255,0.82)', marginTop: 2, display: 'flex', alignItems: 'center' },
  headerStats: { display: 'flex', alignItems: 'center', gap: 8 },
  statBlock: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    minWidth: 66, padding: '5px 12px', borderRadius: 10,
    background: 'rgba(15,23,42,0.16)', border: '1px solid rgba(255,255,255,0.16)',
    backdropFilter: 'blur(4px)',
  },
  statValue: { fontSize: '0.98rem', fontWeight: 800, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: '0.52rem', textTransform: 'uppercase', letterSpacing: '0.09em', color: 'rgba(255,255,255,0.72)', fontWeight: 700, marginTop: 1 },
  body: {
    flex: 1, position: 'relative', minHeight: 0,
    // O mapa passa a ocupar o corpo todo; os painéis flutuam por cima.
  },
  // Painel flutuante de OTs — card glassy sobre o mapa, canto superior
  // esquerdo. Recolhível para libertar o mapa.
  sidebar: {
    position: 'absolute', top: 12, left: 12, zIndex: 6,
    width: 320, maxHeight: 'calc(100% - 24px)',
    background: 'var(--live-card, rgba(255,255,255,0.96))',
    backdropFilter: 'blur(8px)',
    border: '1px solid var(--live-border, rgba(226,232,240,0.9))',
    borderRadius: 14,
    boxShadow: 'var(--live-shadow, 0 10px 32px rgba(15,23,42,0.22))',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  sidebarCollapsed: {
    transform: 'translateX(-110%)',
    opacity: 0,
    pointerEvents: 'none',
  },
  // Botão flutuante para reabrir o painel quando está recolhido.
  sidebarToggle: {
    position: 'absolute', top: 12, left: 12, zIndex: 6,
    background: 'var(--live-card, rgba(255,255,255,0.96))',
    border: '1px solid var(--live-border, rgba(226,232,240,0.9))',
    boxShadow: '0 6px 18px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card, #fff)' },
  },
  sidebarHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid var(--live-border, #e2e8f0)',
    background: 'var(--live-panel-head, linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%))',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  // Painel "motorista em trânsito" — canto inferior direito, compacto.
  firstPersonPanel: {
    // Canto inferior ESQUERDO, acima da timeline (que vive em bottom:12).
    // 130 px de bottom dá folga para a timeline expandida sem chocar.
    // Largura maior agora porque o painel mostra info rica (matrícula,
    // condutor, morada actual, próxima paragem) — não só uma carrinha.
    position: 'absolute', left: 12, bottom: 130, zIndex: 6,
    // Altura para caber: barra de arrastar + header + estado + próxima
    // paragem/progresso + faixa da estrada (mais alta agora).
    // Largura IGUAL ao painel da Sequência (320) para ficarem alinhados.
    width: 320, height: 252,
    borderRadius: 14, overflow: 'hidden',
    boxShadow: '0 10px 28px rgba(15,23,42,0.22)',
    border: '1px solid var(--live-border, #e2e8f0)',
    background: 'var(--live-card, #fff)',
    display: 'flex', flexDirection: 'column',
  },
  // Barra de arrastar (handle) no topo do painel de 1ª pessoa.
  fpHandle: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '7px 8px 7px 12px', cursor: 'move',
    // Cabeçalho CLARO e coerente com os outros painéis (antes era escuro).
    background: 'var(--live-panel-head, linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%))',
    borderBottom: '1px solid var(--live-border, #e2e8f0)',
    flexShrink: 0,
    userSelect: 'none',
  },
  fpHandleLabel: {
    fontSize: '0.62rem', fontWeight: 800, color: 'var(--live-strong, #0f172a)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  // Botão de reabrir (carro) quando o painel está recolhido.
  fpReopen: {
    position: 'absolute', zIndex: 6,
    background: 'var(--live-card, #fff)', border: '1px solid var(--live-border, #e2e8f0)',
    boxShadow: '0 4px 14px rgba(15,23,42,0.18)',
    width: 40, height: 40,
    '&:hover': { background: 'var(--live-card-2, #f8fafc)' },
  },
  // Chip "🏠 Em regresso" — REMOVIDO. Substituído pelo LiveTelemetryCard
  // do canto inferior direito, cujo title bar fica verde a piscar
  // quando route.routeData.phase === 'returning'.
  // ── Cronologia (timeline) flutuante no fundo do mapa ────────────
  // Faixa horizontal full-width (margens 12 px), compacta — só markers
  // de origem → OTs → destino, com ✓ verde quando concluído. O resto da
  // informação por OT já vive na sidebar; aqui o objectivo é ver de
  // relance "estou em que parte da rota" + a % feita.
  timelinePanel: {
    position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 5,
    background: 'var(--live-card, rgba(255,255,255,0.97))',
    backdropFilter: 'blur(10px)',
    border: '1px solid var(--live-border, rgba(226,232,240,0.9))',
    borderRadius: 12,
    boxShadow: '0 8px 22px rgba(15,23,42,0.18)',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  timelinePanelCollapsed: { /* só o header (40 px) */ },
  timelineHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '6px 12px',
    borderBottom: '1px solid var(--live-border, #e2e8f0)',
    background: 'var(--live-panel-head, linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%))',
  },
  timelineHeaderIcon: {
    width: 20, height: 20, borderRadius: 6,
    // Tile neutro e limpo (antes gradiente escuro pesado).
    background: 'var(--live-card-2, #eef2f6)',
    border: '1px solid var(--live-border, #e2e8f0)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--live-muted, #475569)',
  },
  timelineTitle: {
    fontSize: '0.68rem', fontWeight: 800, color: 'var(--live-strong, #0f172a)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  // Chips de percentagem — versão maior, com label explícito. O operador
  // vê de relance "55% concluído · 45% por fazer".
  timelinePctDone: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 9px', borderRadius: 999,
    background: 'rgba(22,163,74,0.12)',
    fontSize: '0.7rem', fontWeight: 800, color: '#15803d',
    fontVariantNumeric: 'tabular-nums',
  },
  timelinePctLeft: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 9px', borderRadius: 999,
    background: 'rgba(148,163,184,0.18)',
    fontSize: '0.7rem', fontWeight: 800, color: 'var(--live-muted, #475569)',
    fontVariantNumeric: 'tabular-nums',
  },
  timelineSub: { fontSize: '0.6rem', color: 'var(--live-muted, #64748b)', fontWeight: 600 },
  timelineBody: {
    overflowX: 'auto', overflowY: 'hidden',
    padding: '12px 22px 8px',
    position: 'relative',
    '&::-webkit-scrollbar': { height: 5 },
    '&::-webkit-scrollbar-thumb': { background: 'var(--live-border, #cbd5e1)', borderRadius: 3 },
  },
  timelineRail: {
    position: 'relative',
    minWidth: '100%',
    display: 'flex', alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingTop: 4,
  },
  timelineRailLine: {
    position: 'absolute',
    top: 13, left: 18, right: 18, height: 3,
    background: 'var(--live-border, #e2e8f0)', borderRadius: 2,
    zIndex: 0,
  },
  timelineRailLineFilled: {
    position: 'absolute',
    top: 13, left: 18, height: 3,
    background: 'linear-gradient(90deg, #16a34a, #22c55e)',
    borderRadius: 2,
    zIndex: 1,
    transition: 'width 0.6s ease',
  },
  // Pino do veículo a deslizar sobre a rail — agora com o ícone REAL
  // (mesmo que está no mapa), não um icon Material genérico.
  timelineVehicle: {
    position: 'absolute', top: -2, zIndex: 4,
    transform: 'translateX(-50%)',
    transition: 'left 0.8s ease',
    pointerEvents: 'none',
    width: 32, height: 32,
    borderRadius: '50%',
    background: 'var(--live-card, #fff)',
    border: '2px solid var(--live-card, #fff)',
    boxShadow: '0 3px 8px rgba(15,23,42,0.28)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  timelineStop: {
    position: 'relative', zIndex: 2,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 3, flex: '1 1 0', minWidth: 56,
    cursor: 'pointer',
    transition: 'transform 0.15s',
    '&:hover': { transform: 'translateY(-1px)' },
  },
  timelineMarker: {
    width: 22, height: 22, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.62rem', fontWeight: 800, color: '#fff',
    border: '2px solid var(--live-card, #fff)',
    boxShadow: '0 2px 5px rgba(15,23,42,0.22)',
  },
  timelineStopLabel: {
    fontSize: '0.56rem', fontWeight: 700, color: 'var(--live-strong, #0f172a)',
    textAlign: 'center', lineHeight: 1.15,
    maxWidth: 110,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  timelineStopSub: {
    fontSize: '0.5rem', color: 'var(--live-muted, #64748b)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  sidebarTitle: {
    fontSize: '0.66rem', fontWeight: 700, color: 'var(--live-muted, #475569)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  otList: { flex: 1, overflowY: 'auto', padding: '6px 8px 12px' },
  otRow: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '8px 10px', borderRadius: 10,
    marginBottom: 4, cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'all 0.15s',
    '&:hover': { background: '#f8fafc', borderColor: '#e2e8f0' },
  },
  otRowSelected: {
    background: 'rgba(59,130,246,0.08) !important',
    borderColor: '#bfdbfe !important',
  },
  otRail: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    width: 24, flexShrink: 0,
    paddingTop: 2,
  },
  otBullet: {
    width: 24, height: 24, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 800, color: '#fff', flexShrink: 0,
  },
  otRailLine: {
    flex: 1, width: 2, marginTop: 2,
    background: 'linear-gradient(to bottom, #cbd5e1, #cbd5e1)',
    minHeight: 8,
  },
  otBody: { flex: 1, minWidth: 0 },
  otNumber: { fontSize: '0.7rem', fontWeight: 800, color: 'var(--live-strong, #0f172a)' },
  otAddr: { fontSize: '0.62rem', color: 'var(--live-muted, #475569)', marginTop: 1, lineHeight: 1.3 },
  otMeta: {
    fontSize: '0.56rem', fontWeight: 700,
    marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap',
  },
  mapWrap: { position: 'absolute', inset: 0, minWidth: 0 },
  mapOverlay: {
    position: 'absolute', top: 12, right: 12, zIndex: 5,
    background: 'rgba(15,23,42,0.85)', color: '#fff',
    borderRadius: 10, padding: '6px 12px',
    fontSize: '0.66rem', fontWeight: 700,
    display: 'flex', alignItems: 'center', gap: 8,
    backdropFilter: 'blur(6px)',
    boxShadow: '0 4px 12px rgba(15,23,42,0.18)',
  },
  liveDot: {
    width: 8, height: 8, borderRadius: '50%',
    boxShadow: '0 0 0 2px rgba(255,255,255,0.5)',
  },
  // Chip de paragem prolongada — surge sob o estado de GPS.
  stoppedChip: {
    position: 'absolute', top: 48, right: 12, zIndex: 6,
    background: 'rgba(245,158,11,0.96)', color: '#fff',
    borderRadius: 10, padding: '6px 12px',
    fontSize: '0.66rem', fontWeight: 800,
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
  },
  loading: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: 12, color: 'var(--live-muted, #64748b)',
  },
  // Botões recentrar/ajuda — subidos para acima do painel de 1ª pessoa
  // (188px alto + 12px margem = 200px), senão ficavam escondidos.
  centerBtn: {
    position: 'absolute', bottom: 212, right: 14, zIndex: 7,
    background: 'var(--live-card, #fff)', borderRadius: 8,
    boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card-2, #f1f5f9)' },
  },
  helpBtn: {
    position: 'absolute', bottom: 212, right: 56, zIndex: 7,
    background: 'var(--live-card, #fff)', borderRadius: 8,
    boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card-2, #f1f5f9)' },
  },
  trafficBtn: {
    position: 'absolute', bottom: 212, right: 98, zIndex: 7,
    background: 'var(--live-card, #fff)', borderRadius: 8,
    boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card-2, #f1f5f9)' },
  },
  layersBtn: {
    position: 'absolute', bottom: 212, right: 140, zIndex: 7,
    background: 'var(--live-card, #fff)', borderRadius: 8,
    boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card-2, #f1f5f9)' },
  },
  incidentsBtn: {
    position: 'absolute', bottom: 212, right: 182, zIndex: 7,
    background: 'var(--live-card, #fff)', borderRadius: 8,
    boxShadow: '0 2px 6px rgba(15,23,42,0.18)',
    '&:hover': { background: 'var(--live-card-2, #f1f5f9)' },
  },
  incidentsChip: {
    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
    zIndex: 6, padding: '6px 12px', borderRadius: 999,
    color: '#fff', fontSize: '0.7rem', fontWeight: 700,
    boxShadow: '0 4px 14px rgba(15,23,42,0.2)', pointerEvents: 'none',
  },
  // Card vermelho — mensagem entrante do motorista. Aparece em cima
  // ao centro do mapa durante 12 s e força a atenção do operador.
  driverMsgCard: {
    position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
    zIndex: 8, padding: '10px 14px',
    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    color: '#fff', borderRadius: 12,
    border: '2px solid rgba(255,255,255,0.7)',
    boxShadow: '0 10px 28px rgba(220,38,38,0.5)',
    display: 'flex', alignItems: 'center', gap: 12,
    minWidth: 340, maxWidth: 560,
    animation: '$driverMsgPulse 1.6s ease-out infinite',
  },
  driverMsgIcon: {
    fontSize: 24, lineHeight: 1, flexShrink: 0,
  },
  driverMsgBody: {
    flex: 1, minWidth: 0,
  },
  driverMsgTitle: {
    fontSize: '0.66rem', fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    opacity: 0.95,
  },
  driverMsgText: {
    fontSize: '0.8rem', fontWeight: 700,
    overflow: 'hidden', textOverflow: 'ellipsis',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
  },
  driverMsgBtn: {
    background: 'var(--live-card, #fff)', color: '#b91c1c',
    border: 'none', borderRadius: 8,
    padding: '6px 12px', fontSize: '0.7rem', fontWeight: 800,
    cursor: 'pointer', flexShrink: 0,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    transition: 'background 0.12s',
    '&:hover': { background: '#fef2f2' },
  },
  driverMsgClose: {
    color: '#fff', cursor: 'pointer', padding: 2, flexShrink: 0,
    opacity: 0.85, fontSize: 18, lineHeight: 1,
    '&:hover': { opacity: 1 },
  },
  '@keyframes driverMsgPulse': {
    '0%':   { boxShadow: '0 10px 28px rgba(220,38,38,0.5), 0 0 0 0 rgba(220,38,38,0.6)' },
    '70%':  { boxShadow: '0 10px 28px rgba(220,38,38,0.5), 0 0 0 16px rgba(220,38,38,0)' },
    '100%': { boxShadow: '0 10px 28px rgba(220,38,38,0.5), 0 0 0 0 rgba(220,38,38,0)' },
  },
  helpPanel: {
    position: 'absolute', bottom: 254, right: 14, zIndex: 7,
    width: 320,
    background: 'var(--live-card, #fff)',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(15,23,42,0.22)',
    padding: '14px 16px',
    border: '1px solid var(--live-border, #e2e8f0)',
  },
  helpTitle: {
    fontSize: '0.78rem', fontWeight: 800, color: 'var(--live-strong, #0f172a)',
    marginBottom: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  helpRow: {
    display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8,
    fontSize: '0.7rem', color: 'var(--live-text, #334155)', lineHeight: 1.35,
  },
  helpSwatchSolid: {
    width: 22, height: 4, borderRadius: 2, background: '#1d4ed8',
    flexShrink: 0, marginTop: 6,
  },
  helpSwatchGreen: {
    width: 22, height: 4, flexShrink: 0, marginTop: 6,
    backgroundImage: 'linear-gradient(to right, #16a34a 50%, transparent 50%)',
    backgroundSize: '6px 4px', backgroundRepeat: 'repeat-x',
  },
  helpSwatchRed: {
    width: 22, height: 4, flexShrink: 0, marginTop: 6,
    backgroundImage: 'linear-gradient(to right, #dc2626 50%, transparent 50%)',
    backgroundSize: '6px 4px', backgroundRepeat: 'repeat-x',
  },
  progressBar: {
    width: '100%', minWidth: 54, height: 5, borderRadius: 999,
    background: 'rgba(255,255,255,0.25)', overflow: 'hidden', marginTop: 4,
  },
  progressFill: {
    height: '100%', background: '#22c55e', transition: 'width 0.4s',
  },
}));

// OT status → visual config for the timeline rail.
// Ordem de avaliação:
//   delivered → "Concluída/Entregue" (verde)
//   arrivedAt + serviceStartedAt → "Em serviço" (âmbar) — Fase 1B start-service
//   arrivedAt (sem início) → "No local" (azul)
//   in_transit → "Em trânsito" (laranja)
//   default → "Por fazer" (cinzento)
// O sub-estado "Em serviço" distingue à distância o motorista que já está
// a executar o trabalho do que apenas chegou e ainda está à espera.
function otVisualState(ot) {
  if (ot.status === 'delivered') {
    return { kind: 'done', color: '#16a34a', icon: <CheckCircleIcon style={{ fontSize: 14 }} />, label: ot.serviceType === 'assistance' ? 'Concluída' : 'Entregue' };
  }
  if (ot.assistanceReport?.arrivedAt && ot.assistanceReport?.serviceStartedAt) {
    return { kind: 'in_service', color: '#f59e0b', icon: <PlayArrowIcon style={{ fontSize: 14 }} />, label: 'Em serviço' };
  }
  if (ot.assistanceReport?.arrivedAt) {
    return { kind: 'onsite', color: '#0ea5e9', icon: <RoomIcon style={{ fontSize: 14 }} />, label: 'No local' };
  }
  if (ot.status === 'in_transit') {
    return { kind: 'transit', color: '#f59e0b', icon: <PlayArrowIcon style={{ fontSize: 14 }} />, label: 'Em trânsito' };
  }
  return { kind: 'pending', color: '#94a3b8', icon: <HourglassEmptyIcon style={{ fontSize: 12 }} />, label: 'Por fazer' };
}

// Formata o tempo decorrido desde um instante em "MMm SSs" — usado pelo
// contador de "Em serviço" no card da sequência (alinha com o que o
// motorista vê na app móvel: mesmo padrão).
function fmtElapsed(fromIso, now) {
  if (!fromIso) return '';
  const start = new Date(fromIso).getTime();
  if (!Number.isFinite(start)) return '';
  const sec = Math.max(0, Math.round((now - start) / 1000));
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${mm}m ${String(ss).padStart(2, '0')}s`;
}

function buildOTPin({ index, state, selected }) {
  if (!window.google?.maps) return null;
  const fill = state.color;
  const ring = selected ? '#0b1220' : '#ffffff';
  const ringW = selected ? 2.5 : 1.6;
  const w = 30, h = 38;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 30 38">
    <defs><filter id="ot${index}" x="-20%" y="-10%" width="140%" height="125%">
      <feDropShadow dx="0" dy="1" stdDeviation="1.0" flood-color="#0b1220" flood-opacity="0.30"/>
    </filter></defs>
    <g filter="url(#ot${index})">
      <path d="M15 0C7.27 0 1 6.27 1 14c0 10.5 14 24 14 24s14-13.5 14-24C29 6.27 22.73 0 15 0z" fill="${fill}" stroke="${ring}" stroke-width="${ringW}"/>
      <circle cx="15" cy="13" r="9" fill="#ffffff"/>
      <text x="15" y="13.6" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif"
        font-weight="800" font-size="12" fill="${fill}">${index}</text>
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h),
  };
}

// Constrói o ícone SVG do veículo no live monitor — mesmo estilo do mapa
// principal (bubble + cauda + truck icon) mas com 2 diferenças:
//   • Cor base vermelha (rota em curso, sempre live)
//   • Bubble com matrícula E nome do motorista quando a fonte de posição
//     é Traccar ou Both; quando é só APP, mostra apenas o motorista
//     (não há matrícula a confirmar — a posição vem do telefone do
//     motorista, não do dispositivo do veículo).
//
// Args:
//   plate      — matrícula do veículo (string ou null)
//   driverName — nome do motorista (string ou null)
//   source     — 'app' | 'traccar' | null (fonte actual da posição)
//   isLive     — boolean, controla cor de fundo (vermelho fresh, âmbar stale)
// Marcador do veículo no Live — ícone EcooGPS do tipo de veículo (igual
// ao mapa de planeamento) + perna + bolha com a etiqueta. A etiqueta:
// fonte 'app' → só o motorista (a posição vem do telemóvel dele);
// 'traccar'/ambos → matrícula · motorista. Estado: verde em movimento,
// amarelo parado, cinzento sem sinal recente.
const LONG_STOP_MS = 2 * 60 * 60 * 1000; // 2h — pedido Move Express: parado ≥2h → vermelho
function buildVehicleIcon({ plate, driverName, source, isLive, type, heading, speed, stoppedMs } = {}) {
  if (!window.google?.maps) return null;
  const driverShort = (driverName || '').split(/\s+/).slice(0, 2).join(' ').slice(0, 14);
  const plateUp = (plate || '').toUpperCase();
  const showPlate = source !== 'app' && !!plateUp;
  const label = showPlate
    ? `${plateUp} · ${driverShort || '—'}`
    : (driverShort || plateUp || '—');
  // Estado: sem sinal → cinzento; parado ≥2h → VERMELHO (sinaliza paragem
  // prolongada); a andar → verde; parado (curto) → amarelo.
  const longStop = Number.isFinite(stoppedMs) && stoppedMs >= LONG_STOP_MS;
  const status = !isLive ? 'gray' : longStop ? 'red' : (Number(speed) > 3 ? 'green' : 'yellow');
  const mi = buildVehicleMarkerIcon({ label, type, status, heading });
  return {
    url: mi.url,
    scaledSize: new window.google.maps.Size(mi.w, mi.h),
    anchor: new window.google.maps.Point(mi.ax, mi.ay),
  };
}

// Marcador de PARTIDA/CHEGADA — mesmo pino "teardrop" do mapa de planeamento
// (MapViewGoogle.buildEndpointIcon): verde com ⚐ (partida) / vermelho com ⚑
// (chegada). Assim os pontos da rota ficam iguais no live e no planeamento.
function buildEndpointMarkerIcon({ color, glyph, w = 26, h = 34 }) {
  if (!window.google?.maps) return null;
  const pin = 'M17 0C7.61 0 0 7.61 0 17c0 12.75 17 27 17 27s17-14.25 17-27C34 7.61 26.39 0 17 0z';
  const cx = 17, cy = 15, r = 9.5;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 34 44">
    <defs><filter id="epsh" x="-20%" y="-10%" width="140%" height="125%">
      <feDropShadow dx="0" dy="1" stdDeviation="0.9" flood-color="#0f172a" flood-opacity="0.35"/>
    </filter></defs>
    <g filter="url(#epsh)">
      <path d="${pin}" fill="${color}"/>
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="#ffffff"/>
      <text x="${cx}" y="${cy + 0.8}" text-anchor="middle" dominant-baseline="central"
        font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"
        font-weight="800" font-size="13" fill="${color}">${glyph}</text>
    </g>
  </svg>`;
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(w, h),
    anchor: new window.google.maps.Point(w / 2, h),
  };
}

/**
 * LiveRouteMonitor — full-screen split-view monitor for an in-progress route.
 *
 * Left panel: ordered list of OTs with a timeline rail (✓ done, 📍 on site,
 * ▶ in transit, ⏳ pending), each row clickable to centre the map.
 *
 * Right panel: Google Maps with three layers:
 *   • Planned route (blue polyline, from routeData.computedRoute.geometry)
 *   • Actual GPS trail (green dashed polyline, from /actual-trail Traccar)
 *   • Live vehicle marker (red circle, from vehicles slice via WS)
 * Plus numbered OT pins coloured by their current state.
 *
 * Refresh strategy:
 *   • WS triggers immediate refetch of the route detail (order:arrived,
 *     order:delivered, route:status) — keeps the timeline in sync.
 *   • Trail polled every 30s — Traccar's history endpoint is rate-limited.
 *   • Route detail polled every 60s as a backstop in case WS drops.
 */
export default function LiveRouteMonitor({ routeId, onClose }) {
  const classes = useStyles();
  const liveTheme = usePlannerTheme(); // 'light' | 'dark' (segue o avatar)
  const { token } = useContext(AuthContext);
  const { lang } = useSelector(selectUI);
  const vehicles = useSelector(s => s.vehicles?.list || []);
  const dispatchRedux = useDispatch();
  const pt = lang === 'pt';

  const [route, setRoute] = useState(null);
  const [trail, setTrail] = useState([]);
  // Live tracking metadata — surfaces the active source (app vs traccar)
  // to the operator. Filled by /live-tracking; updated by WS pushes when
  // the app reports a new fix.
  const [tracking, setTracking] = useState({
    source: null, sourceConfig: 'both', position: null,
    app: null, traccar: null,
  });
  const [loading, setLoading] = useState(true);
  const [selectedOTId, setSelectedOTId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // Painel de OTs flutuante — recolhível para libertar o mapa.
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Cronologia da rota (timeline flutuante no fundo do mapa). Aberta
  // por defeito; o operador pode recolher para um chip de 44 px.
  const [timelineOpen, setTimelineOpen] = useState(true);
  // Painel "veículo em trânsito" (1ª pessoa): aberto por defeito; pode ser
  // recolhido para um botão de carro, e arrastado livremente pelo mapa.
  const [firstPersonOpen, setFirstPersonOpen] = useState(true);
  const [fpPos, setFpPos] = useState(null); // { left, bottom } px após arrastar; null = posição por defeito
  // Chat operador ↔ motorista — diálogo + contador de não lidas enquanto
  // o painel está fechado.
  const [chatOpen, setChatOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false); // replay do percurso real
  const [mapToolPos, setMapToolPos] = useState(null); // toolbar flutuante dos botões do mapa {left,top}|null
  const [chatUnread, setChatUnread] = useState(0);
  const chatOpenRef = useRef(false);
  // #2 — camada de trânsito Google (vermelho/amarelo/verde) sobre o mapa.
  const [trafficOn, setTrafficOn] = useState(false);
  // Tipo de mapa — cicla mapa / satélite / relevo (botão de camadas).
  const [mapType, setMapType] = useState('roadmap');
  // Re-render quando um ícone de veículo (EcooGPS) acaba de carregar.
  const [, setIconTick] = useState(0);
  useEffect(() => onVehicleIconLoaded(() => setIconTick(t => t + 1)), []);
  // #2 — incidentes de trânsito (HERE + TomTom): acidentes, vias cortadas, obras.
  const [showIncidents, setShowIncidents] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const incidentsFetchTimerRef = useRef(null);
  // Detecção de paragem prolongada — instante em que o veículo parou.
  const [stoppedSince, setStoppedSince] = useState(null);
  const [vehPopup, setVehPopup] = useState(false); // popup de info da viatura (clique no marcador)
  const mapRef = useRef(null);
  const fittedRef = useRef(false);
  // Último fixTime Traccar já acrescentado ao trajecto — evita repetir o
  // mesmo ponto enquanto o veículo está parado (vehicles:update repete-o).
  const lastTraccarFixRef = useRef(null);
  // Esta instância corre numa janela própria (#/live/...) ou embebida no
  // Planner? Em janela própria não faz sentido o botão "abrir em janela".
  const isStandalone = typeof window !== 'undefined'
    && (window.location.hash || '').startsWith('#/live');

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  const fetchRoute = useCallback(async () => {
    if (!token || !routeId) return;
    try {
      const res = await fetch(`/planner-api/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // The route detail endpoint doesn't include OTs — fetch them by routeId.
        const otsRes = await fetch(`/planner-api/transport-orders?assignedRouteId=${routeId}&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (otsRes.ok) {
          const otsData = await otsRes.json();
          data.transportOrders = Array.isArray(otsData) ? otsData : (otsData.data || []);
        } else {
          data.transportOrders = [];
        }
        setRoute(data);
      }
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token, routeId]);

  // Unified live-tracking fetch: position + trail + active source, all
  // resolved server-side based on the client's `liveTrackingSource`
  // setting and freshness of each feed.
  const fetchTracking = useCallback(async () => {
    if (!token || !routeId) return;
    try {
      const res = await fetch(`/planner-api/routes/${routeId}/live-tracking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const points = despikeTrail(
          (data?.trail || [])
            .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
            .map(p => ({ lat: p.lat, lng: p.lng, t: p.timestamp || p.t || p.fixTime || null }))
        );
        // Don't wipe a previously-shown trail just because a single
        // refresh tick came back empty — that can happen for a few
        // seconds when the route auto-completes and the EcooGPS
        // window-end timestamp shifts. Keep what was on screen until
        // a non-empty response or a definitive 400 says otherwise.
        if (points.length > 0) {
          setTrail(points);
        }
        setTracking({
          source: data.source || null,
          sourceConfig: data.sourceConfig || 'both',
          position: data.position || null,
          app: data.app || null,
          traccar: data.traccar || null,
        });
      } else if (res.status === 400) {
        setTrail([]);
      }
    } catch (e) { /* silent */ }
  }, [token, routeId]);

  // Acrescenta um ponto ao trajecto em tempo real (vindo de um evento WS),
  // sem ir ao /live-tracking. Faz dedupe do último ponto e volta a passar
  // o despike por cima — um fix mau não contamina a linha.
  const appendTrailPoint = useCallback((lat, lng, t) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setTrail(prev => {
      const last = prev[prev.length - 1];
      if (last && last.lat === lat && last.lng === lng) return prev;
      return despikeTrail([...prev, { lat, lng, t: t || new Date().toISOString() }]);
    });
  }, []);

  // Initial load + periodic backstop refresh.
  useEffect(() => {
    fetchRoute();
    fetchTracking();
    const r = setInterval(fetchRoute, ROUTE_REFRESH_MS);
    const t = setInterval(fetchTracking, TRAIL_REFRESH_MS);
    return () => { clearInterval(r); clearInterval(t); };
  }, [fetchRoute, fetchTracking]);

  // WS handlers — immediate refresh on relevant events.
  useEffect(() => {
    const sock = getSocket();
    if (!sock) return;
    const refresh = () => fetchRoute();
    const refreshAll = () => { fetchRoute(); fetchTracking(); };
    // Deviation only matters for the route currently open in the monitor.
    const onDeviation = (data) => {
      if (Number(data?.routeId) === Number(routeId)) fetchRoute();
    };
    // Driver app pushed a new GPS fix — acrescenta o ponto directamente
    // do payload do evento (já traz lat/lng/speed/heading/timestamp), sem
    // ir ao /live-tracking. Marcador e trajecto actualizam de imediato.
    // Ignorado quando o cliente está fixado em Traccar.
    const onDriverPosition = (data) => {
      if (Number(data?.routeId) !== Number(routeId)) return;
      if (tracking.sourceConfig === 'traccar') return;
      const p = data?.position || {};
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const fixTime = p.timestamp || new Date().toISOString();
      appendTrailPoint(lat, lng, fixTime);
      setTracking(prev => ({
        ...prev,
        source: 'app',
        position: {
          lat, lng, fixTime, ageSec: 0,
          speed: p.speed ?? 0,
          heading: p.heading ?? 0,
        },
      }));
    };
    // Motorista escolheu/mudou a app de navegação — refresca a rota
    // para a chip de "Navegação: Google/Waze" actualizar.
    const onNavApp = (data) => {
      if (Number(data?.routeId) === Number(routeId)) fetchRoute();
    };
    sock.on('order:arrived', refresh);
    sock.on('order:service_started', refresh);
    sock.on('order:delivered', refresh);
    sock.on('order:status', refresh);
    sock.on('route:status', refreshAll);
    sock.on('route:deviation', onDeviation);
    sock.on('driver:position', onDriverPosition);
    sock.on('route:nav-app', onNavApp);
    return () => {
      sock.off('order:arrived', refresh);
      sock.off('order:service_started', refresh);
      sock.off('order:delivered', refresh);
      sock.off('order:status', refresh);
      sock.off('route:status', refreshAll);
      sock.off('route:deviation', onDeviation);
      sock.off('driver:position', onDriverPosition);
      sock.off('route:nav-app', onNavApp);
    };
  }, [fetchRoute, fetchTracking, routeId, appendTrailPoint, tracking.sourceConfig]);

  // Trajecto Traccar em tempo real — acrescenta o fix do veículo da rota
  // a partir do stream partilhado `vehicles:update` (1 poll server-side a
  // cada 5s, comum a todas as janelas). Zero polling por janela. Só corre
  // quando a fonte é (ou pode ser) Traccar; o dedupe por fixTime evita
  // repetir pontos com o veículo parado.
  useEffect(() => {
    if (tracking.sourceConfig === 'app') return;
    if (tracking.source && tracking.source !== 'traccar') return;
    if (!route) return;
    const vehId = route.vehicleId || route.vehicle?.id;
    const plate = route.vehicle?.plate || route.vehiclePlate;
    const veh = vehId
      ? vehicles.find(v => Number(v.id) === Number(vehId))
      : (plate ? vehicles.find(v => (v.plate || '').toUpperCase() === String(plate).toUpperCase()) : null);
    const pos = veh?.position;
    const lat = Number(pos?.latitude);
    const lng = Number(pos?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const fix = pos.fixTime || null;
    if (fix && fix === lastTraccarFixRef.current) return;
    lastTraccarFixRef.current = fix;
    appendTrailPoint(lat, lng, fix);
    // Move também o marcador — senão ficaria congelado entre as
    // reconciliações de 2 min (o /live-tracking é que costumava actualizá-lo).
    const ageSec = fix ? Math.max(0, Math.round((Date.now() - new Date(fix).getTime()) / 1000)) : 0;
    setTracking(prev => ({
      ...prev,
      source: 'traccar',
      position: {
        lat, lng, fixTime: fix || new Date().toISOString(), ageSec,
        // vehicles:update traz a velocidade Traccar em nós → km/h.
        speed: pos.speed ? Math.round(pos.speed * 1.852) : 0,
        heading: pos.course ?? pos.heading ?? 0,
      },
    }));
  }, [vehicles, route, tracking.source, tracking.sourceConfig, appendTrailPoint]);

  // Chat — conta mensagens novas do motorista enquanto o painel está
  // fechado, para o badge no botão de chat. O ChatDriverDialog trata da
  // thread em si; aqui só nos interessa o sininho.
  // Mensagem recente do motorista — mostra-se um card vermelho destacado
  // no topo do mapa durante 12 s para o operador perceber sem ter de
  // procurar o sininho. Auto-dismiss + botão "Abrir chat".
  const [recentDriverMsg, setRecentDriverMsg] = useState(null);
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);
  useEffect(() => {
    const sock = getSocket();
    if (!sock) return;
    const onMsg = (m) => {
      const did = route?.driver?.id
        || route?.routeDrivers?.[0]?.driver?.id
        || route?.routeData?.driverId;
      if (!did || Number(m?.driverId) !== Number(did)) return;
      if (!m?.fromAdmin && !chatOpenRef.current) {
        setChatUnread(c => c + 1);
        // Card vermelho persistente no topo do mapa.
        setRecentDriverMsg({
          body: String(m.body || ''),
          driverName: route?.driver?.name || (pt ? 'Motorista' : 'Driver'),
          at: Date.now(),
          id: m.id || Math.random(),
        });
        // #3 — toast imediato; o botão de chat passa a piscar.
        dispatchRedux(addToast({
          type: 'info', severity: 'info',
          message: `💬 ${route?.driver?.name || 'Motorista'}: ${String(m.body || '').slice(0, 80)}`,
        }));
      }
    };
    sock.on('message:new', onMsg);
    return () => { sock.off('message:new', onMsg); };
  }, [route, pt, dispatchRedux]);

  // Auto-dismiss do card vermelho — 12 s para o operador ter tempo
  // de o ler sem o card ficar a tapar o mapa para sempre.
  useEffect(() => {
    if (!recentDriverMsg) return;
    const t = setTimeout(() => setRecentDriverMsg(null), 12000);
    return () => clearTimeout(t);
  }, [recentDriverMsg]);

  // Resolve live position from the unified `tracking` payload (server
  // already picked the best source based on the client config + freshness).
  // Falls back to the vehicles slice (Traccar via WS) when the endpoint
  // hasn't replied yet — keeps the marker visible during the first 1-2s.
  const livePos = useMemo(() => {
    if (tracking.position && Number.isFinite(tracking.position.lat) && Number.isFinite(tracking.position.lng)) {
      const ageSec = tracking.position.ageSec ?? Infinity;
      return {
        lat: tracking.position.lat,
        lng: tracking.position.lng,
        ageSec,
        speed: tracking.position.speed,
        heading: tracking.position.heading ?? tracking.position.course ?? null,
        live: ageSec < 1200, // "sem sinal" só ≥20min (viatura parada continua a reportar)
        source: tracking.source,
      };
    }
    // Fallback (only used while the first /live-tracking is in flight).
    if (!route) return null;
    const vehId = route.vehicleId || route.vehicle?.id;
    const plate = route.vehicle?.plate || route.vehiclePlate;
    const veh = vehId
      ? vehicles.find(v => Number(v.id) === Number(vehId))
      : (plate ? vehicles.find(v => (v.plate || '').toUpperCase() === plate.toUpperCase()) : null);
    if (!veh?.position?.latitude || !veh?.position?.longitude) return null;
    const lastFix = veh.position.fixTime ? new Date(veh.position.fixTime).getTime() : 0;
    const ageSec = lastFix ? Math.round((Date.now() - lastFix) / 1000) : Infinity;
    return {
      lat: Number(veh.position.latitude),
      lng: Number(veh.position.longitude),
      ageSec,
      // vehicles:update traz a velocidade Traccar em nós → km/h.
      speed: veh.position.speed ? Math.round(veh.position.speed * 1.852) : 0,
      heading: veh.position.course ?? veh.position.heading ?? null,
      live: ageSec < 1200, // "sem sinal" só ≥20min (viatura parada continua a reportar)
      source: 'traccar',
    };
  }, [tracking, route, vehicles]);

  // Objeto COMPLETO da viatura (Redux/WS) — traz os atributos extra do EcooGPS
  // (morada, tensão/power, km/odometer, motion+motionTime) usados no popup e no
  // estado vermelho de paragem prolongada.
  const liveVehicle = useMemo(() => {
    if (!route) return null;
    const vehId = route.vehicleId || route.vehicle?.id;
    const plate = route.vehicle?.plate || route.vehiclePlate;
    return vehId
      ? vehicles.find(v => Number(v.id) === Number(vehId))
      : (plate ? vehicles.find(v => (v.plate || '').toUpperCase() === String(plate).toUpperCase()) : null);
  }, [route, vehicles]);

  // Há quanto tempo está parada (ms). Preferimos o `motionTime` do EcooGPS (o
  // instante REAL em que parou — o mesmo do "Estacionado há Xh" do ecooGPS);
  // fallback ao stoppedSince local. null quando está a andar.
  const stoppedMs = useMemo(() => {
    if ((livePos?.speed || 0) >= 3) return null;
    const attrs = liveVehicle?.position?.attributes || {};
    if (attrs.motion === false && attrs.motionTime) {
      const t = new Date(attrs.motionTime).getTime();
      if (Number.isFinite(t)) return Math.max(0, Date.now() - t);
    }
    if (stoppedSince) return Math.max(0, Date.now() - stoppedSince);
    return null;
  }, [liveVehicle, livePos, stoppedSince]);

  // #2 — paragem prolongada: marca o instante em que o veículo parou
  // (velocidade < 3 km/h com GPS recente). Limpa assim que volta a andar.
  useEffect(() => {
    const moving = (livePos?.speed || 0) >= 3;
    if (!livePos?.live || moving) {
      setStoppedSince(null);
    } else {
      setStoppedSince(prev => prev || Date.now());
    }
  }, [livePos]);

  // #2 — incidentes de trânsito (HERE + TomTom). Quando ligado, busca os
  // incidentes do viewport e refresca ao mexer no mapa (debounce 500ms).
  useEffect(() => {
    if (!mapRef.current || !isLoaded) return undefined;
    if (!showIncidents) {
      setIncidents([]);
      setSelectedIncident(null);
      return undefined;
    }
    let cancelled = false;
    async function refresh() {
      if (!mapRef.current) return;
      const bounds = mapRef.current.getBounds();
      if (!bounds) return;
      setIncidentsLoading(true);
      try {
        const [hereList, tomtomList] = await Promise.all([
          fetchIncidentsForViewport(bounds).catch(() => []),
          fetchIncidentsTomTomBbox(bounds).catch(() => []),
        ]);
        const taggedHere = (hereList || []).map(x => ({ ...x, source: 'here' }));
        const DUP = 0.0005; // ≈55 m
        const merged = [...taggedHere];
        for (const tt of (tomtomList || [])) {
          const dupe = merged.findIndex(x =>
            Math.abs(x.coordinates[0] - tt.coordinates[0]) < DUP &&
            Math.abs(x.coordinates[1] - tt.coordinates[1]) < DUP);
          if (dupe === -1) merged.push(tt);
          else merged[dupe] = { ...merged[dupe], alsoReportedBy: 'tomtom' };
        }
        if (!cancelled) setIncidents(merged);
      } catch {
        if (!cancelled) setIncidents([]);
      } finally {
        if (!cancelled) setIncidentsLoading(false);
      }
    }
    refresh();
    const idle = mapRef.current.addListener('idle', () => {
      if (incidentsFetchTimerRef.current) clearTimeout(incidentsFetchTimerRef.current);
      incidentsFetchTimerRef.current = setTimeout(refresh, 500);
    });
    return () => {
      cancelled = true;
      if (incidentsFetchTimerRef.current) clearTimeout(incidentsFetchTimerRef.current);
      if (idle) idle.remove();
    };
  }, [showIncidents, isLoaded]);

  // Planned route polyline (sampled).
  const plannedPath = useMemo(() => {
    const coords = route?.routeData?.computedRoute?.geometry?.coordinates || [];
    if (coords.length === 0) return [];
    const MAX = 600;
    if (coords.length <= MAX) return coords.map(c => ({ lat: c[1], lng: c[0] }));
    const step = Math.ceil(coords.length / MAX);
    const out = [];
    for (let i = 0; i < coords.length; i += step) out.push({ lat: coords[i][1], lng: coords[i][0] });
    out.push({ lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] });
    return out;
  }, [route]);

  // OTs sorted by their delivery window (earliest first) — same heuristic
  // used by the consolidator, so the rail order matches the planned visit order.
  const sortedOTs = useMemo(() => {
    const list = [...(route?.transportOrders || [])];
    // Ordem da rota: posição de cada OT na sequência real de paragens
    // (waypoints + destino, todos com otId). Antes ordenava por janela
    // de entrega — para OT de assistência sem janela o comparador dava
    // NaN (ordem indefinida) e os pins ficavam com números trocados.
    const seq = [];
    (route?.waypoints || []).forEach(w => { if (w?.otId != null) seq.push(Number(w.otId)); });
    if (route?.destination?.otId != null) seq.push(Number(route.destination.otId));
    const rank = new Map(seq.map((id, i) => [id, i]));
    list.sort((a, b) => {
      const ra = rank.has(Number(a.id)) ? rank.get(Number(a.id)) : Infinity;
      const rb = rank.has(Number(b.id)) ? rank.get(Number(b.id)) : Infinity;
      if (ra !== rb) return ra - rb;
      // Desempate (OT fora da sequência) — por janela de entrega.
      const ta = a.deliveryWindowFrom ? new Date(a.deliveryWindowFrom).getTime() : Infinity;
      const tb = b.deliveryWindowFrom ? new Date(b.deliveryWindowFrom).getTime() : Infinity;
      return ta === tb ? 0 : ta - tb;
    });
    return list;
  }, [route]);

  // Rota de combustível (Move Express) — o motorista NÃO usa app, por isso o
  // chat com o motorista não faz sentido e é escondido no Live.
  const isFuelRoute = useMemo(
    () => sortedOTs.some(o => o.serviceType === 'fuel') || tracking.sourceConfig === 'traccar',
    [sortedOTs, tracking.sourceConfig],
  );

  // Progress + elapsed/remaining.
  const stats = useMemo(() => {
    const total = sortedOTs.length;
    const delivered = sortedOTs.filter(o => o.status === 'delivered').length;
    const onSite = sortedOTs.filter(o => o.status !== 'delivered' && o.assistanceReport?.arrivedAt).length;
    const pct = total > 0 ? Math.round((delivered / total) * 100) : 0;
    const startedAt = route?.routeData?.actualStartedAt
      ? new Date(route.routeData.actualStartedAt).getTime()
      : (route?.actualStartedAt ? new Date(route.actualStartedAt).getTime() : null);
    const elapsedSec = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : null;
    const remainingSec = route?.totalDurationS && elapsedSec != null
      ? Math.max(0, route.totalDurationS - elapsedSec)
      : null;
    return { total, delivered, onSite, pct, elapsedSec, remainingSec };
  }, [route, sortedOTs]);

  // Tick to recompute elapsed/remaining without refetching.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Ticker dedicado para o contador "Em serviço · MMm SSs" no card da
  // sequência — 1 Hz, mas só corre quando há pelo menos uma OT em serviço
  // (arrivedAt + serviceStartedAt sem deliveredAt). Evita re-render
  // desnecessário noutros estados da rota.
  const [serviceNow, setServiceNow] = useState(() => Date.now());
  const anyInService = useMemo(
    () => sortedOTs.some(o => o.assistanceReport?.arrivedAt
      && o.assistanceReport?.serviceStartedAt
      && o.status !== 'delivered'),
    [sortedOTs],
  );
  useEffect(() => {
    if (!anyInService) return undefined;
    const id = setInterval(() => setServiceNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anyInService]);

  // ─── Deviation detection ─────────────────────────────────────────
  // Tracks the live position against the planned polyline and raises an
  // alert (+ POSTs to the backend for persistence) when the driver has
  // been off-route for longer than the configured threshold.
  //
  // State shape:
  //   active:      currently flagged as off-route (true once enterSeconds passed)
  //   firstOffAt:  first time the driver crossed enterMeters (resets on return)
  //   maxDistanceM: worst distance seen during the current excursion
  //   lastReportAt: last POST timestamp (for throttle)
  //   lastDistance: most recent live distance to the planned line
  const [deviation, setDeviation] = useState({
    active: false,
    firstOffAt: null,
    maxDistanceM: 0,
    lastReportAt: 0,
    lastDistance: null,
  });
  // Persisted history (from the route's routeData.deviations) — surfaced
  // in the sidebar as a "log" so the operator can see past incidents even
  // after the driver returned to the planned line.
  const persistedDeviations = useMemo(
    () => Array.isArray(route?.routeData?.deviations) ? route.routeData.deviations : [],
    [route]
  );

  useEffect(() => {
    if (!livePos || plannedPath.length < 2) return;
    // If the vehicle is parked (speed < 5 km/h), don't accumulate a
    // deviation. Cars at the depot, on a rest break, or paused on-site
    // sit at a fixed offset from the route polyline forever — without
    // this guard the monitor would scream "off-route" 90s after every
    // stop, even when the driver is doing exactly what they should.
    // We DO clear an active deviation though, in case the driver pulled
    // over after going off-route (the recovery is genuine).
    const isMoving = (livePos.speed || 0) >= 5;
    const dist = pointToPolylineMeters(
      { lat: livePos.lat, lng: livePos.lng },
      plannedPath
    );
    setDeviation((prev) => {
      if (!isMoving) {
        // Parked: keep prev.active state but never *become* active.
        // Reset firstOffAt so the 90s clock doesn't tick while idle.
        return prev.active
          ? { ...prev, lastDistance: dist }
          : { active: false, firstOffAt: null, maxDistanceM: 0, lastReportAt: 0, lastDistance: dist };
      }
      const now = Date.now();
      const isOff = dist > DEVIATION_CONFIG.enterMeters;
      const isBack = dist < DEVIATION_CONFIG.exitMeters;

      // Currently active deviation that came back inside the corridor.
      if (prev.active && isBack) {
        return { active: false, firstOffAt: null, maxDistanceM: 0, lastReportAt: 0, lastDistance: dist };
      }

      // First time off-route (or already off-route, just update max).
      if (isOff) {
        const firstOffAt = prev.firstOffAt || now;
        const elapsedSec = (now - firstOffAt) / 1000;
        const maxDistanceM = Math.max(prev.maxDistanceM || 0, dist);
        const justActivated = !prev.active && elapsedSec >= DEVIATION_CONFIG.enterSeconds;

        // Fire the POST when crossing the threshold OR while active and
        // the throttle window has expired (to keep the persisted record
        // up-to-date with the worst distance seen so far).
        const shouldReport = justActivated
          || (prev.active && now - prev.lastReportAt > DEVIATION_CONFIG.reportThrottleMs);
        if (shouldReport && token && routeId) {
          fetch(`/planner-api/routes/${routeId}/deviation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              distanceM: Math.round(maxDistanceM),
              durationS: Math.round(elapsedSec),
              position: { lat: livePos.lat, lng: livePos.lng },
              startedAt: new Date(firstOffAt).toISOString(),
            }),
          }).catch(() => { /* silent — UI already shows the alert */ });
        }
        return {
          active: prev.active || justActivated,
          firstOffAt,
          maxDistanceM,
          lastReportAt: shouldReport ? now : prev.lastReportAt,
          lastDistance: dist,
        };
      }

      // Inside the corridor but had a partial deviation — reset the
      // accumulator (operator clearly recovered course before the threshold).
      if (prev.firstOffAt && !prev.active) {
        return { active: false, firstOffAt: null, maxDistanceM: 0, lastReportAt: 0, lastDistance: dist };
      }
      return { ...prev, lastDistance: dist };
    });
  }, [livePos, plannedPath, routeId, token]);

  // Classifica o trail em troços contínuos, cada um marcado on/off-route.
  // Quebra o troço sempre que:
  //   · a classificação on/off muda, OU
  //   · a velocidade implícita entre dois fixes consecutivos é impossível
  //     para um veículo rodoviário (>200 km/h) — apanha "teleportes" de
  //     fixes GPS maus independentemente da distância (ex.: 1.4 km em 7s).
  //   · sem timestamps fiáveis, recorre a um limiar de distância (1.2 km).
  // Usa `visualOffMeters` (80 m) — separado do threshold de alerta (300 m),
  // para o operador ver TODO o desvio no mapa, mesmo os curtos que não
  // disparam alerta. Depois descarta troços off-route minúsculos
  // (<60 m de extensão), que são aglomerados de fixes maus.
  const MAX_KMH = 200;
  const NOTIME_JUMP_M = 1200;
  const trailSegments = useMemo(() => {
    if (trail.length < 2 || plannedPath.length < 2) {
      return { pieces: trail.length > 1 ? [{ off: false, path: trail }] : [] };
    }
    const pieces = [];
    let cur = null;
    let prev = null;
    for (const p of trail) {
      const off = pointToPolylineMeters(p, plannedPath) > DEVIATION_CONFIG.visualOffMeters;
      let bigJump = false;
      if (prev) {
        const distM = haversineM(prev.lat, prev.lng, p.lat, p.lng);
        const dtS = (prev.t && p.t)
          ? Math.abs(new Date(p.t).getTime() - new Date(prev.t).getTime()) / 1000
          : null;
        if (dtS != null && dtS > 0) {
          bigJump = (distM / dtS) * 3.6 > MAX_KMH; // m/s → km/h
        } else {
          bigJump = distM > NOTIME_JUMP_M;
        }
      }
      if (!cur || cur.off !== off || bigJump) {
        if (cur && !bigJump && prev) cur.path.push(prev);
        cur = { off, path: bigJump || !prev ? [] : [prev] };
        pieces.push(cur);
      }
      cur.path.push(p);
      prev = p;
    }
    // Extensão (diagonal da bounding box) de um troço, em metros.
    const spanM = (path) => {
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      for (const q of path) {
        if (q.lat < minLat) minLat = q.lat; if (q.lat > maxLat) maxLat = q.lat;
        if (q.lng < minLng) minLng = q.lng; if (q.lng > maxLng) maxLng = q.lng;
      }
      return haversineM(minLat, minLng, maxLat, maxLng);
    };
    const segs = pieces
      .filter(pc => pc.path.length > 1)
      // Aglomerado off-route minúsculo = cluster de fixes GPS maus parados
      // no mesmo sítio errado. Threshold baixado para 60 m (era 200 m)
      // agora que o limiar visual é 80 m — caso contrário desvios reais
      // mas curtos (atalhos de 100–150 m) ficavam silenciados.
      .filter(pc => !(pc.off && spanM(pc.path) < 60));
    return {
      pieces: segs,
      onRoute: segs.filter(s => !s.off).flatMap(s => s.path),
      offRoute: segs.filter(s => s.off).flatMap(s => s.path),
    };
  }, [trail, plannedPath]);

  // Distância percorrida (metros) — soma haversine entre fixes do trail,
  // sem despique adicional (o `trail` já vem despicado pela ingestão).
  // Usada pelo HUD direito (LiveTelemetryCard) para mostrar progresso em km.
  const distanceTravelledM = useMemo(() => {
    if (!Array.isArray(trail) || trail.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < trail.length; i++) {
      const a = trail[i - 1], b = trail[i];
      total += haversineM(a.lat, a.lng, b.lat, b.lng);
    }
    return total;
  }, [trail]);

  // Initial fit-bounds — once the planned path is loaded, frame everything
  // (route + trail + vehicle). Subsequent fits would yank the operator's
  // pan/zoom around so we only do it once.
  useEffect(() => {
    if (!isLoaded || !mapRef.current || fittedRef.current) return;
    if (plannedPath.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    plannedPath.forEach(p => bounds.extend(p));
    sortedOTs.forEach(o => {
      if (o.deliveryLat && o.deliveryLng) bounds.extend({ lat: Number(o.deliveryLat), lng: Number(o.deliveryLng) });
    });
    if (livePos) bounds.extend(livePos);
    mapRef.current.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
    fittedRef.current = true;
  }, [isLoaded, plannedPath, sortedOTs, livePos]);

  // Recenter button — re-fits bounds on demand (e.g. after operator panned
  // away). Inclui todas as referências da rota: plannedPath, OTs, trail
  // real do veículo, posição actual e origem/destino. Padding direito
  // alargado deixa espaço ao LiveTelemetryCard (canto inferior direito);
  // padding inferior depende de a cronologia estar aberta ou colapsada
  // — sem isso a rota desaparecia por trás da timeline.
  const handleRecenter = () => {
    if (!mapRef.current || !window.google?.maps) return;
    fittedRef.current = false;
    const bounds = new window.google.maps.LatLngBounds();
    let extended = false;
    const addLL = (p) => {
      if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      bounds.extend({ lat: p.lat, lng: p.lng });
      extended = true;
    };
    plannedPath.forEach(addLL);
    sortedOTs.forEach(o => {
      if (Number.isFinite(Number(o.deliveryLat)) && Number.isFinite(Number(o.deliveryLng))) {
        addLL({ lat: Number(o.deliveryLat), lng: Number(o.deliveryLng) });
      }
    });
    (Array.isArray(trail) ? trail : []).forEach(addLL);
    if (livePos) addLL(livePos);
    if (Array.isArray(route?.origin?.coordinates) && route.origin.coordinates.length >= 2) {
      addLL({ lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] });
    }
    if (Array.isArray(route?.destination?.coordinates) && route.destination.coordinates.length >= 2) {
      addLL({ lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] });
    }
    if (!extended) return;
    mapRef.current.fitBounds(bounds, {
      top: 80,
      bottom: timelineOpen ? 200 : 80,
      left: 60,
      right: 280, // espaço para o LiveTelemetryCard
    });
    fittedRef.current = true;
  };

  // Click on an OT row → pan to its delivery point.
  const handleSelectOT = (ot) => {
    setSelectedOTId(ot.id);
    if (mapRef.current && ot.deliveryLat && ot.deliveryLng) {
      mapRef.current.panTo({ lat: Number(ot.deliveryLat), lng: Number(ot.deliveryLng) });
      mapRef.current.setZoom(Math.max(mapRef.current.getZoom() || 12, 14));
    }
  };

  // Retirar OT da rota mantendo veículo/motorista atribuídos.
  // Espelha exactamente o handler do OrdersView para consistência. Abre
  // ConfirmDialog estilizado (sem window.confirm).
  const [removeOTConfirm, setRemoveOTConfirm] = useState(null); // OT pendente

  const handleRemoveOT = (ev, ot) => {
    ev.stopPropagation();
    if (!token || !ot?.id || !route?.id) return;
    setRemoveOTConfirm(ot);
  };

  const performRemoveOT = async () => {
    const ot = removeOTConfirm;
    if (!ot || !route?.id) return;
    const otRef = ot.customRef || ot.orderNumber || `OT-${ot.id}`;
    const routeRef = route.name || `Rota #${route.id}`;
    try {
      const res = await fetch(`/planner-api/routes/${route.id}/remove-ot`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ otId: ot.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Falha');
      }
      dispatchRedux(addToast({
        type: 'success',
        message: pt ? `✓ ${otRef} retirada de ${routeRef}` : `✓ ${otRef} removed from ${routeRef}`,
        duration: 5000,
      }));
      if (typeof fetchRoute === 'function') fetchRoute();
    } catch (e) {
      dispatchRedux(addToast({
        type: 'error',
        message: (pt ? 'Erro: ' : 'Error: ') + (e?.message || e),
      }));
    }
  };

  if (loading) {
    return (
      <Box className={classes.root} data-live-theme={liveTheme}>
        <Box className={classes.loading}>
          <CircularProgress size={28} style={{ color: '#3b82f6' }} />
          <Typography style={{ fontSize: '0.78rem' }}>{pt ? 'A carregar rota…' : 'Loading…'}</Typography>
        </Box>
      </Box>
    );
  }

  if (!route) {
    return (
      <Box className={classes.root} data-live-theme={liveTheme}>
        <Box className={classes.loading}>
          <Typography style={{ fontSize: '0.84rem', fontWeight: 700 }}>{pt ? 'Rota não encontrada' : 'Route not found'}</Typography>
          <Button onClick={onClose} variant="contained" color="primary" size="small">{pt ? 'Voltar' : 'Back'}</Button>
        </Box>
      </Box>
    );
  }

  const driverName = route.driver?.name || route.routeData?.driverName || '—';
  const plate = route.vehicle?.plate || route.vehiclePlate || route.routeData?.vehiclePlate || '—';
  // Tipo de veículo (categoria EcooGPS) — para o ícone do marcador.
  // Vem do feed `vehicles:update` (o mesmo do mapa de planeamento).
  const liveVehicleType = (() => {
    const vid = route.vehicleId || route.vehicle?.id;
    const veh = vid
      ? vehicles.find(x => Number(x.id) === Number(vid))
      : (vehicles.find(x => (x.plate || '').toUpperCase() === String(plate).toUpperCase()));
    // Na janela própria do monitor o slice `vehicles` (feed WS) pode estar
    // vazio (e a viatura pode nem estar na tabela). Sem fallback, o ícone caía
    // no 'car' por defeito. Usamos o SUBTIPO escolhido na rota
    // (routeOptions.vehicleSubType, ex.: 'cisterna' → tanker) — é o que reflete
    // o tipo real; o `vehicleType` ('hgv') não mapeia bem.
    const opts = route.routeData?.routeOptions || route.routeOptions || {};
    return veh?.type
      || opts.vehicleSubType
      || opts.vehicleType
      || route.vehicle?.type
      || null;
  })();
  // Chat: motorista da rota + OT activa para o cabeçalho do diálogo.
  const driverId = route.driver?.id
    || route.routeDrivers?.[0]?.driver?.id
    || route.routeData?.driverId
    || null;
  const driverPhone = route.driver?.phone
    || route.routeDrivers?.[0]?.driver?.phone
    || null;
  const chatActiveOT = (sortedOTs || []).find(o => otVisualState(o) !== 'done')
    || sortedOTs?.[0] || null;

  return (
    <Box className={classes.root} data-live-theme={liveTheme}>
      {/* Header */}
      <Box className={classes.header}>
        <IconButton size="small" onClick={onClose} className={classes.backBtn}>
          <ArrowBackIcon style={{ fontSize: 20 }} />
        </IconButton>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Typography className={classes.headerTitle} noWrap>
            {route.name || `Rota #${route.id}`}
          </Typography>
          <Typography className={classes.headerMeta}>
            <PersonIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />{driverName}
            <span style={{ margin: '0 6px', opacity: 0.5 }}>·</span>
            <LocalShippingIcon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 3 }} />{plate}
          </Typography>
        </Box>
        <Box className={classes.headerStats}>
          <Box className={classes.statBlock}>
            <Box className={classes.statValue}>{stats.delivered}/{stats.total}</Box>
            <Box className={classes.statLabel}>{pt ? 'Ordens' : 'Orders'}</Box>
            <Box className={classes.progressBar}>
              <Box className={classes.progressFill} style={{ width: `${stats.pct}%` }} />
            </Box>
          </Box>
          <Box className={classes.statBlock}>
            <Box className={classes.statValue}>{fmtDuration(stats.elapsedSec)}</Box>
            <Box className={classes.statLabel}>{pt ? 'Decorrido' : 'Elapsed'}</Box>
          </Box>
          <Box className={classes.statBlock}>
            <Box className={classes.statValue} style={{ color: '#fbbf24' }}>{fmtDuration(stats.remainingSec)}</Box>
            <Box className={classes.statLabel}>{pt ? 'Restante' : 'Remaining'}</Box>
          </Box>
          {/* App de navegação escolhida pelo motorista (#6) — vem de
              route.routeData.driverNavApp, gravada quando o motorista
              lança o Google Maps / Waze a partir da app. */}
          {route?.routeData?.driverNavApp?.app && (() => {
            const nav = route.routeData.driverNavApp;
            const isWaze = nav.app === 'waze';
            return (
              <Tooltip title={pt
                ? `Motorista a navegar com ${isWaze ? 'Waze' : 'Google Maps'}${nav.label ? ` → ${nav.label}` : ''}`
                : `Driver navigating with ${isWaze ? 'Waze' : 'Google Maps'}`}>
                <Box style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: isWaze ? 'rgba(51,204,255,0.18)' : 'rgba(26,115,232,0.18)',
                  border: `1px solid ${isWaze ? 'rgba(51,204,255,0.5)' : 'rgba(26,115,232,0.5)'}`,
                  borderRadius: 999, padding: '3px 10px',
                }}>
                  <span style={{ fontSize: 13 }}>{isWaze ? '🟦' : '🗺️'}</span>
                  <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff' }}>
                    {isWaze ? 'Waze' : 'Google Maps'}
                  </span>
                </Box>
              </Tooltip>
            );
          })()}
          {/* Chip de fonte de tracking — mostra sempre ao operador que
              GPS está a alimentar o live: 📱 App (GPS da app do motorista)
              ou 🚛 Traccar (GPS do veículo). Quando a config é fixa
              (app/traccar) mas ainda não há dados dessa fonte, mostra o
              estado configurado a aguardar sinal. */}
          {(() => {
            const cfg = tracking.sourceConfig || 'both';
            const eff = tracking.source; // fonte efectiva com dados
            // Fonte a apresentar: a efectiva quando há dados; senão a
            // configurada (app/traccar) — em 'both' sem dados não mostra.
            const shown = eff || (cfg !== 'both' ? cfg : null);
            if (!shown) return null;
            const isApp = shown === 'app';
            const waiting = !eff; // configurada mas ainda sem fixes
            return (
              <Tooltip title={isApp
                ? (pt ? 'GPS da app do motorista' : 'Driver app GPS')
                : (pt ? 'GPS do veículo (EcooGPS)' : 'Vehicle GPS (EcooGPS)')}>
                <Box style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 8,
                  background: waiting ? 'rgba(245,158,11,0.28)' : 'rgba(255,255,255,0.18)',
                  fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.02em',
                }}>
                  {isApp ? '📱 App' : '🚛 EcooGPS'}{waiting ? (pt ? ' · à espera' : ' · waiting') : ''}
                </Box>
              </Tooltip>
            );
          })()}
          {deviation.active && (
            <Tooltip title={pt
              ? `Off-route há ${Math.round((Date.now() - deviation.firstOffAt) / 60000)}min · ${Math.round(deviation.maxDistanceM)}m do plano`
              : `Off-route ${Math.round((Date.now() - deviation.firstOffAt) / 60000)}min · ${Math.round(deviation.maxDistanceM)}m off plan`}>
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 8,
                background: 'rgba(239,68,68,0.95)', color: '#fff',
                fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.05em',
                animation: 'devPulse 1.6s ease-in-out infinite',
                boxShadow: '0 0 0 0 rgba(239,68,68,0.4)',
              }}>
                ⚠ {pt ? 'DESVIO' : 'OFF-ROUTE'}
                <span style={{ fontWeight: 600, opacity: 0.85 }}>
                  {Math.round(deviation.maxDistanceM)}m
                </span>
              </Box>
            </Tooltip>
          )}
          {/* Chat com o motorista — escondido em rotas de combustível (sem app). */}
          {driverId && !isFuelRoute && (
            <Tooltip title={pt ? 'Conversar com o motorista' : 'Chat with driver'}>
              <IconButton
                size="small"
                onClick={() => { setChatOpen(true); setChatUnread(0); }}
                className={classes.backBtn}
                style={{
                  borderRadius: '50%',
                  animation: chatUnread > 0 ? 'chatPulse 1.3s ease-in-out infinite' : 'none',
                }}
              >
                <Badge badgeContent={chatUnread} color="error">
                  <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {/* Abrir esta rota numa janela própria — permite acompanhar
              várias rotas em simultâneo. Escondido quando já é janela. */}
          {!isStandalone && (
            <Tooltip title={pt ? 'Abrir em janela própria' : 'Open in own window'}>
              <IconButton
                size="small"
                onClick={() => {
                  const w = openLiveWindow(route.id, token);
                  if (w) { onClose && onClose(); }
                  else dispatchRedux(addToast({
                    type: 'warning', severity: 'warning',
                    message: pt
                      ? 'O browser bloqueou a janela — permite popups para este site.'
                      : 'Popup blocked — allow popups for this site.',
                  }));
                }}
                className={classes.backBtn}
              >
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Replay do percurso REAL — botão em destaque (pílula com texto). */}
          {route?.id && (
            <Tooltip title={pt ? 'Reproduzir o percurso real do veículo' : 'Replay the real vehicle route'}>
              <Button
                size="small"
                onClick={() => setReplayOpen(true)}
                startIcon={<PlayArrowIcon style={{ fontSize: 18, color: '#eab308' }} />}
                style={{
                  textTransform: 'none', fontWeight: 800, fontSize: '0.72rem',
                  color: '#eab308', borderRadius: 999, padding: '4px 14px', minWidth: 0,
                  background: 'var(--live-card, #ffffff)', border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(15,23,42,0.18)',
                }}
              >
                Replay
              </Button>
            </Tooltip>
          )}
          <Tooltip title={pt ? 'Actualizar' : 'Refresh'}>
            <IconButton size="small" onClick={() => { fetchRoute(); fetchTracking(); }} className={classes.backBtn}>
              <RefreshIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Body — mapa em fundo total, painéis flutuantes por cima */}
      <Box className={classes.body}>
        {/* Painel de OTs flutuante (recolhível) */}
        <Box className={`${classes.sidebar} ${sidebarOpen ? '' : classes.sidebarCollapsed}`}>
          <Box className={classes.sidebarHeader}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <Box className={classes.timelineHeaderIcon}><RoomIcon style={{ fontSize: 12 }} /></Box>
              <Typography className={classes.sidebarTitle}>
                {pt ? `Sequência (${sortedOTs.length} OTs)` : `Sequence (${sortedOTs.length} OTs)`}
              </Typography>
            </Box>
            <Tooltip title={pt ? 'Recolher painel' : 'Collapse panel'}>
              <IconButton size="small" onClick={() => setSidebarOpen(false)} style={{ padding: 2 }}>
                <ArrowBackIcon style={{ fontSize: 16, color: 'var(--live-muted, #64748b)' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className={classes.otList}>
            {sortedOTs.map((ot, idx) => {
              const state = otVisualState(ot);
              const selected = selectedOTId === ot.id;
              const arrivedAt = ot.assistanceReport?.arrivedAt;
              const serviceStartedAt = ot.assistanceReport?.serviceStartedAt;
              return (
                <Box
                  key={ot.id}
                  className={`${classes.otRow} ${selected ? classes.otRowSelected : ''}`}
                  onClick={() => handleSelectOT(ot)}
                >
                  <Box className={classes.otRail}>
                    <Box className={classes.otBullet} style={{ background: state.color }}>
                      {state.kind === 'pending' ? (idx + 1) : state.icon}
                    </Box>
                    {idx < sortedOTs.length - 1 && <Box className={classes.otRailLine} />}
                  </Box>
                  <Box className={classes.otBody} style={{ flex: 1, minWidth: 0 }}>
                    <Typography className={classes.otNumber}>
                      {otLabel(ot)}
                    </Typography>
                    <Typography className={classes.otAddr} noWrap>
                      {ot.deliveryName || ot.deliveryAddress || '—'}
                    </Typography>
                    <Box className={classes.otMeta}>
                      <Chip
                        size="small"
                        label={state.label}
                        style={{
                          height: 16, fontSize: '0.52rem', fontWeight: 800,
                          background: `${state.color}1f`, color: state.color,
                        }}
                      />
                      {arrivedAt && state.kind !== 'done' && (
                        <span style={{ color: '#0ea5e9' }}>📍 {fmtTime(arrivedAt)}</span>
                      )}
                      {/* Início de serviço — só visível enquanto a OT está
                          em serviço (sem deliveredAt). Contador a correr
                          em mm:ss alinhado com o que o motorista vê. */}
                      {serviceStartedAt && state.kind === 'in_service' && (
                        <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                          🛠 {fmtTime(serviceStartedAt)} · {fmtElapsed(serviceStartedAt, serviceNow)}
                        </span>
                      )}
                      {ot.deliveredAt && (
                        <span style={{ color: '#16a34a' }}>✔ {fmtTime(ot.deliveredAt)}</span>
                      )}
                    </Box>
                  </Box>
                  {/* Botão × — retirar OT da rota mantendo veículo/motorista.
                      Só visível em rotas draft/planned para OTs ainda não
                      in_transit/delivered (essas são imutáveis ou exigem
                      Fase 2). Para in_progress o backend rejeita; aqui
                      escondemos para evitar UX confuso. */}
                  {(route?.status === 'draft' || route?.status === 'planned') &&
                   ot.status !== 'in_transit' && ot.status !== 'delivered' && (
                    <Tooltip title={pt ? 'Retirar da rota (manter veículo)' : 'Remove from route (keep vehicle)'}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRemoveOT(e, ot)}
                        style={{
                          padding: 4, marginLeft: 6,
                          color: '#dc2626',
                          background: 'rgba(220,38,38,0.06)',
                          flexShrink: 0,
                        }}
                      >
                        <CloseIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
            {sortedOTs.length === 0 && (
              <Typography style={{ fontSize: '0.7rem', color: 'var(--live-muted2, #94a3b8)', textAlign: 'center', padding: 20 }}>
                {pt ? 'Sem OTs nesta rota' : 'No OTs on this route'}
              </Typography>
            )}

            {/* Pseudo-linha "🏠 Regresso a casa" no fim da sequência.
                Aparece quando a rota tem o flag `phase=returning` activo
                (todas as OTs concluídas e o motorista a regressar). Mostra
                o tempo decorrido desde que entrou no regresso. Sem ela o
                operador não sabia, pela sequência, que o motorista já tinha
                terminado e estava de regresso — só via no HUD da direita. */}
            {route?.routeData?.phase === 'returning'
              && (route?.status === 'in_progress' || route?.dbStatus === 'in_progress') && (() => {
              const since = route?.routeData?.returningSince ? new Date(route.routeData.returningSince) : null;
              const minSince = since ? Math.max(0, Math.round((Date.now() - since.getTime()) / 60000)) : null;
              return (
                <Box className={classes.otRow} style={{ cursor: 'default' }}>
                  <Box className={classes.otRail}>
                    <Box className={classes.otBullet} style={{ background: '#16a34a' }}>
                      <span style={{ fontSize: 12 }}>🏠</span>
                    </Box>
                  </Box>
                  <Box className={classes.otBody} style={{ flex: 1, minWidth: 0 }}>
                    <Typography className={classes.otNumber}>
                      {pt ? 'Regresso a casa' : 'Returning home'}
                    </Typography>
                    <Typography className={classes.otAddr} noWrap>
                      {pt
                        ? 'Todas as OTs concluídas — motorista a regressar'
                        : 'All OTs completed — driver returning'}
                    </Typography>
                    <Box className={classes.otMeta}>
                      <Chip
                        size="small"
                        label={pt ? 'EM REGRESSO' : 'RETURNING'}
                        style={{
                          height: 16, fontSize: '0.52rem', fontWeight: 800,
                          background: 'rgba(22,163,74,0.18)', color: '#16a34a',
                        }}
                      />
                      {since && (
                        <span style={{ color: '#16a34a' }}>
                          🏠 {fmtTime(since)}{minSince != null ? ` · ${minSince} min` : ''}
                        </span>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })()}

            {/* Deviations log — shown only when there's at least one
                recorded incident. Each row: hora · pico de distância ·
                duração total. The persisted list comes from routeData
                and is kept up to date by the backend's deduplication
                window (within 30s = same incident). */}
            {persistedDeviations.length > 0 && (
              <>
                <Box style={{
                  margin: '12px 0 6px', padding: '6px 8px',
                  borderTop: '1px solid #fecaca',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ fontSize: 12 }}>⚠</span>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 800, color: '#991b1b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {pt ? `Desvios (${persistedDeviations.length})` : `Deviations (${persistedDeviations.length})`}
                  </Typography>
                </Box>
                {[...persistedDeviations]
                  .sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt))
                  .map((dev, i) => {
                    const mins = Math.round((dev.durationS || 0) / 60);
                    return (
                      <Box key={`${dev.startedAt}-${i}`} style={{
                        margin: '4px 4px', padding: '6px 10px',
                        background: '#fef2f2', borderLeft: '3px solid #dc2626',
                        borderRadius: 6,
                      }}>
                        <Typography style={{ fontSize: '0.66rem', fontWeight: 800, color: '#991b1b' }}>
                          {fmtTime(dev.startedAt)} · {Math.round(dev.distanceM || 0)} m
                        </Typography>
                        <Typography style={{ fontSize: '0.56rem', color: '#7f1d1d', marginTop: 1 }}>
                          {pt
                            ? `Fora da rota durante ${mins > 0 ? mins + 'min' : Math.round(dev.durationS || 0) + 's'}`
                            : `Off-route for ${mins > 0 ? mins + 'min' : Math.round(dev.durationS || 0) + 's'}`}
                        </Typography>
                      </Box>
                    );
                  })}
              </>
            )}
          </Box>
        </Box>

        {/* Botão para reabrir o painel de OTs quando recolhido */}
        {!sidebarOpen && (
          <Tooltip title={pt ? 'Mostrar painel de OTs' : 'Show OT panel'}>
            <IconButton size="small" className={classes.sidebarToggle} onClick={() => setSidebarOpen(true)}>
              <LocalShippingIcon style={{ fontSize: 18, color: '#1e3a8a' }} />
            </IconButton>
          </Tooltip>
        )}

        <Box className={classes.mapWrap}>
          {isLoaded ? (() => {
            // Setas de direcção (sentido da marcha) sobre as rotas — pontas
            // brancas repetidas ao longo da linha. Só quando a API já carregou.
            const dirArrows = (window.google && window.google.maps) ? [{
              // Triângulo cheio branco com contorno escuro, PEQUENO e ESPAÇADO —
              // marca a direcção sem carregar a linha (antes ficava denso/confuso).
              icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.1, fillColor: '#ffffff', fillOpacity: 1, strokeColor: '#0f172a', strokeWeight: 1 },
              offset: '0', repeat: '180px',
            }] : undefined;
            return (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={DEFAULT_CENTER}
              zoom={6}
              options={MAP_OPTIONS}
              mapTypeId={mapType}
              onLoad={(m) => { mapRef.current = m; }}
            >
              {/* Planned route — blue polyline */}
              {plannedPath.length > 1 && (
                <Polyline
                  path={plannedPath}
                  options={{
                    strokeColor: '#1d4ed8',
                    strokeOpacity: 0.9,
                    strokeWeight: 4,
                    geodesic: false,
                    clickable: false,
                    icons: dirArrows,
                  }}
                />
              )}
              {/* Trajecto real — desenhado troço a troço, com linhas
                  SÓLIDAS para máxima visibilidade. Verde dentro do
                  corredor da rota, vermelho fora (desvio). Cada troço
                  é uma polyline separada: saltos GPS implausíveis
                  quebram o troço em vez de desenharem uma linha recta
                  para o nada. */}
              {trailSegments.pieces.map((pc, i) => (
                <Polyline
                  key={`trail-${i}`}
                  path={pc.path}
                  options={{
                    strokeColor: pc.off ? '#dc2626' : '#16a34a',
                    strokeOpacity: 0.95,
                    strokeWeight: pc.off ? 6 : 5,
                    geodesic: false,
                    clickable: false,
                    zIndex: pc.off ? 12 : 11, // desvios por cima do verde
                    // Setas de direcção só nos troços VERDES (percurso dentro
                    // do corredor); os vermelhos (desvio) ficam limpos para não
                    // confundir. Triângulo cheio branco = distinguível.
                    icons: (!pc.off && pc.path && pc.path.length > 2) ? dirArrows : undefined,
                  }}
                />
              ))}
              {/* #2 — camada de trânsito Google em tempo real (verde/
                  amarelo/vermelho conforme a fluidez nas estradas) */}
              {trafficOn && <TrafficLayer />}
              {/* #2 — incidentes de trânsito (HERE + TomTom) */}
              {showIncidents && incidents.map(inc => {
                const cfg = inc.typeName || INCIDENT_TYPE_MAP[inc.type] || INCIDENT_TYPE_MAP[4];
                return (
                  <Marker
                    key={`inc-${inc.id}`}
                    position={{ lat: inc.coordinates[1], lng: inc.coordinates[0] }}
                    icon={{
                      path: 'M12 2L1 21h22L12 2z',
                      fillColor: cfg.color, fillOpacity: 0.95,
                      strokeColor: '#fff', strokeWeight: 1.5, scale: 1.3,
                      anchor: new window.google.maps.Point(12, 21),
                      labelOrigin: new window.google.maps.Point(12, 14),
                    }}
                    label={{ text: cfg.emoji, fontSize: '11px' }}
                    zIndex={520}
                    onClick={() => setSelectedIncident(inc)}
                  />
                );
              })}
              {showIncidents && selectedIncident && (() => {
                const cfg = selectedIncident.typeName
                  || INCIDENT_TYPE_MAP[selectedIncident.type] || INCIDENT_TYPE_MAP[4];
                return (
                  <InfoWindow
                    position={{ lat: selectedIncident.coordinates[1], lng: selectedIncident.coordinates[0] }}
                    onCloseClick={() => setSelectedIncident(null)}
                  >
                    <Box style={{ maxWidth: 250, fontSize: '0.72rem' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 18 }}>{cfg.emoji}</span>
                        <b style={{ color: 'var(--live-strong, #0f172a)' }}>{cfg.label}</b>
                        {selectedIncident.roadClosed && (
                          <span style={{ background: '#dc2626', color: '#fff', borderRadius: 4, padding: '1px 5px', fontSize: '0.58rem', fontWeight: 800 }}>
                            VIA CORTADA
                          </span>
                        )}
                      </Box>
                      <div style={{ color: 'var(--live-muted, #475569)', lineHeight: 1.4 }}>
                        {selectedIncident.summary || selectedIncident.description || '—'}
                      </div>
                      <div style={{ color: 'var(--live-muted2, #94a3b8)', marginTop: 4, fontSize: '0.62rem' }}>
                        Fonte: {selectedIncident.source === 'tomtom' ? 'TomTom' : 'HERE'}
                        {selectedIncident.alsoReportedBy ? ` · confirmado por ${String(selectedIncident.alsoReportedBy).toUpperCase()}` : ''}
                      </div>
                    </Box>
                  </InfoWindow>
                );
              })()}
              {/* Partida (origem) e Chegada (destino) — mesmos pinos do
                  planeamento (⚐ verde / ⚑ vermelha). Ficam por baixo dos pinos
                  das OT (zIndex menor) para não os taparem quando coincidem. */}
              {Array.isArray(route?.origin?.coordinates) && route.origin.coordinates.length >= 2 && (
                <Marker
                  position={{ lat: Number(route.origin.coordinates[1]), lng: Number(route.origin.coordinates[0]) }}
                  icon={buildEndpointMarkerIcon({ color: '#16a34a', glyph: '⚐' })}
                  title={route.origin.name || (pt ? 'Partida' : 'Start')}
                  zIndex={400}
                />
              )}
              {Array.isArray(route?.destination?.coordinates) && route.destination.coordinates.length >= 2 && (
                <Marker
                  position={{ lat: Number(route.destination.coordinates[1]), lng: Number(route.destination.coordinates[0]) }}
                  icon={buildEndpointMarkerIcon({ color: '#dc2626', glyph: '⚑' })}
                  title={route.destination.name || (pt ? 'Chegada' : 'End')}
                  zIndex={400}
                />
              )}
              {/* OT pins */}
              {sortedOTs.map((ot, idx) => {
                const lat = Number(ot.deliveryLat);
                const lng = Number(ot.deliveryLng);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const state = otVisualState(ot);
                const icon = buildOTPin({ index: idx + 1, state, selected: selectedOTId === ot.id });
                return icon ? (
                  <Marker
                    key={ot.id}
                    position={{ lat, lng }}
                    icon={icon}
                    title={`${otLabel(ot)} · ${state.label}`}
                    onClick={() => handleSelectOT(ot)}
                    zIndex={600}
                  />
                ) : null;
              })}
              {/* Live vehicle marker — bubble com matrícula+motorista
                  (estilo do mapa principal). Quando a fonte da posição
                  é 'app' (só APP) mostra apenas o motorista, já que a
                  matrícula não é o que está a reportar. Para 'traccar'
                  ou 'both' mostra ambos. */}
              {livePos && (
                <Marker
                  position={{ lat: livePos.lat, lng: livePos.lng }}
                  icon={buildVehicleIcon({
                    plate: route?.vehicle?.plate || route?.vehiclePlate,
                    driverName: route?.driver?.name
                      || route?.routeDrivers?.[0]?.driver?.name
                      || route?.routeData?.driverName,
                    source: livePos.source,
                    isLive: !!livePos.live,
                    type: liveVehicleType,
                    heading: livePos.heading,
                    speed: livePos.speed,
                    stoppedMs,
                  })}
                  onClick={() => setVehPopup(true)}
                  title={pt ? 'Clicar para ver detalhes da viatura' : 'Click for vehicle details'}
                />
              )}
              {/* Balão a ACOMPANHAR o veículo — igual ao do histórico: rosa dos
                  ventos + velocidade/hora/rumo, com a seta (tail do InfoWindow)
                  a apontar para o veículo. Segue o marcador ao panning. */}
              {livePos && Number.isFinite(Number(livePos.heading)) && (() => {
                const hd = ((Number(livePos.heading) || 0) % 360 + 360) % 360;
                const card = (lang === 'pt'
                  ? ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO']
                  : ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'])[Math.round(hd / 45) % 8];
                const spd = Math.max(0, Math.round(Number(livePos.speed) || 0));
                const ts = livePos.fixTime || livePos.t || Date.now();
                const hhmm = new Date(ts).toLocaleTimeString(lang === 'pt' ? 'pt-PT' : 'en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                return (
                  <InfoWindow
                    position={{ lat: livePos.lat, lng: livePos.lng }}
                    options={{ disableAutoPan: true, pixelOffset: window.google?.maps ? new window.google.maps.Size(0, -34) : undefined }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '3px 6px 2px', fontFamily: 'Roboto, sans-serif' }}>
                      <CompassRose heading={hd} lang={lang} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderLeft: '1px solid #eef2f7', paddingLeft: 11 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, color: spd > 3 ? '#15803d' : '#b91c1c' }}>
                          {spd} <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--live-muted2, #94a3b8)' }}>km/h</span>
                        </div>
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--live-strong, #0f172a)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                          🕐 {hhmm}
                        </div>
                        <div style={{ fontSize: 9.5, fontWeight: 800, color: '#2563eb', letterSpacing: 0.3 }}>{card} · {Math.round(hd)}°</div>
                      </div>
                    </div>
                  </InfoWindow>
                );
              })()}
              {/* Popup de informações da viatura (estilo ecooGPS) — abre ao
                  clicar no marcador. */}
              {livePos && vehPopup && (() => {
                const attrs = liveVehicle?.position?.attributes || {};
                const plate = (route?.vehicle?.plate || route?.vehiclePlate || '—');
                const model = route?.vehicle?.model || liveVehicle?.model || liveVehicle?.type || '';
                const drvName = route?.driver?.name || route?.routeDrivers?.[0]?.driver?.name || route?.routeData?.driverName || null;
                const addr = liveVehicle?.position?.address || null;
                const volt = attrs.power != null ? Number(attrs.power) : null;
                const odoM = attrs.odometer != null ? Number(attrs.odometer) : null;
                const km = odoM != null ? (odoM > 100000 ? Math.round(odoM / 1000) : Math.round(odoM)) : null;
                const fmtDur = (ms) => { const m = Math.round(ms / 60000); if (m < 60) return `${m} min`; const h = Math.floor(m / 60), mm = m % 60; return mm > 0 ? `${h}h${String(mm).padStart(2, '0')}` : `${h}h`; };
                const row = (icon, val, color) => val != null && val !== '' ? (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
                    <span style={{ fontSize: 13, width: 16, textAlign: 'center' }}>{icon}</span>
                    <Typography style={{ fontSize: '0.72rem', color: color || '#334155', fontWeight: 600 }}>{val}</Typography>
                  </Box>
                ) : null;
                return (
                  <InfoWindow position={{ lat: livePos.lat, lng: livePos.lng }} onCloseClick={() => setVehPopup(false)}>
                    <Box style={{ minWidth: 210, maxWidth: 280, padding: '2px 2px 4px' }}>
                      <Typography style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--live-strong, #0f172a)' }}>
                        {plate}{model ? ` · ${model}` : ''}
                      </Typography>
                      {row('👤', drvName && driverPhone ? `${drvName} (${driverPhone})` : (drvName || null))}
                      {row('📍', addr)}
                      {row('🧭', `${livePos.lat.toFixed(6)}, ${livePos.lng.toFixed(6)}`, '#64748b')}
                      {row('⏱️', (livePos.speed || 0) >= 3
                        ? `${Math.round(livePos.speed)} km/h`
                        : (stoppedMs != null
                          ? `${pt ? 'Estacionado há' : 'Parked for'} ${fmtDur(stoppedMs)}`
                          : `0 km/h`),
                        (livePos.speed || 0) < 3 && stoppedMs >= LONG_STOP_MS ? '#dc2626' : '#334155')}
                      {row('🔋', volt != null ? `${volt.toFixed(1)} V` : null)}
                      {row('🛣️', km != null ? `${km.toLocaleString(pt ? 'pt-PT' : 'en-GB')} km` : null)}
                      {!livePos.live && (
                        <Typography style={{ fontSize: '0.64rem', color: '#dc2626', fontWeight: 700, marginTop: 6 }}>
                          {pt ? '⚠ Sem sinal GPS recente' : '⚠ No recent GPS signal'}
                        </Typography>
                      )}
                    </Box>
                  </InfoWindow>
                );
              })()}
            </GoogleMap>
            );
          })()
          : (
            <Box className={classes.loading}>
              <CircularProgress size={24} style={{ color: '#3b82f6' }} />
              <Typography style={{ fontSize: '0.74rem' }}>{pt ? 'A carregar mapa…' : 'Loading map…'}</Typography>
            </Box>
          )}

          {/* GPS status overlay */}
          <Box className={classes.mapOverlay}>
            <Box
              className={classes.liveDot}
              style={{ background: livePos?.live ? '#22c55e' : '#f59e0b' }}
            />
            <span>
              {livePos
                ? (livePos.live
                    ? (pt ? `GPS activo · ${livePos.ageSec}s` : `GPS live · ${livePos.ageSec}s`)
                    : (pt ? `GPS sem sinal recente` : 'GPS stale'))
                : (pt ? 'Sem GPS' : 'No GPS')}
            </span>
            {trail.length > 1 && (
              <span style={{ opacity: 0.7, marginLeft: 6 }}>
                · {trail.length} pts
              </span>
            )}
          </Box>

          {/* Card vermelho — mensagem entrante do motorista. Pisca para
              chamar a atenção, auto-fecha em 12 s; o "Abrir chat" abre
              o ChatDriverDialog directamente na conversa do motorista
              da rota actual. */}
          {recentDriverMsg && (
            <Box className={classes.driverMsgCard}>
              <span className={classes.driverMsgIcon}>💬</span>
              <Box className={classes.driverMsgBody}>
                <Typography className={classes.driverMsgTitle}>
                  {pt ? 'Mensagem do motorista' : 'Driver message'} · {recentDriverMsg.driverName}
                </Typography>
                <Typography className={classes.driverMsgText}>
                  {recentDriverMsg.body}
                </Typography>
              </Box>
              <button
                className={classes.driverMsgBtn}
                onClick={() => { setChatOpen(true); setRecentDriverMsg(null); }}
              >
                {pt ? 'Abrir chat' : 'Open chat'}
              </button>
              <span
                className={classes.driverMsgClose}
                onClick={() => setRecentDriverMsg(null)}
                role="button"
                aria-label={pt ? 'Fechar' : 'Close'}
              >×</span>
            </Box>
          )}

          {/* #2 — paragem prolongada (>2 min parado com GPS recente) */}
          {stoppedSince && (Date.now() - stoppedSince) > 120000 && (
            <Box className={classes.stoppedChip}>
              ⚠ {pt
                ? `Parado há ${Math.round((Date.now() - stoppedSince) / 60000)} min`
                : `Stopped for ${Math.round((Date.now() - stoppedSince) / 60000)} min`}
            </Box>
          )}

          {/* #2 — estado da consulta de incidentes */}
          {showIncidents && (
            <Box
              className={classes.incidentsChip}
              style={{
                background: incidentsLoading ? 'rgba(15,23,42,0.85)'
                  : (incidents.length > 0 ? 'rgba(220,38,38,0.95)' : 'rgba(22,163,74,0.95)'),
              }}
            >
              {incidentsLoading
                ? '⏳ A consultar HERE + TomTom…'
                : incidents.length > 0
                  ? `⚠ ${incidents.length} ${incidents.length === 1 ? 'incidente' : 'incidentes'} nesta zona`
                  : '✓ Sem incidentes nesta zona'}
            </Box>
          )}

          {/* Recenter button */}
          {/* Toolbar FLUTUANTE e ARRASTÁVEL dos botões do mapa (centrar /
              legenda / trânsito / camadas / acidentes). Ancorada em baixo à
              direita (acompanha a cronologia); ao arrastar pela pega fica livre. */}
          {(() => {
            const anchored = !mapToolPos;
            const startToolDrag = (e) => {
              e.preventDefault();
              const el = e.currentTarget.parentElement;
              const parent = el.offsetParent ? el.offsetParent.getBoundingClientRect() : { left: 0, top: 0 };
              const rect = el.getBoundingClientRect();
              const baseLeft = mapToolPos ? mapToolPos.left : (rect.left - parent.left);
              const baseTop = mapToolPos ? mapToolPos.top : (rect.top - parent.top);
              const startX = e.clientX, startY = e.clientY;
              const move = (ev) => setMapToolPos({
                left: Math.max(0, baseLeft + (ev.clientX - startX)),
                top: Math.max(0, baseTop + (ev.clientY - startY)),
              });
              const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
              window.addEventListener('pointermove', move);
              window.addEventListener('pointerup', up);
            };
            const btn = (onClick, title, icon) => (
              <IconButton size="small" onClick={onClick} title={title}
                style={{ borderRadius: 9, padding: 6 }}>{icon}</IconButton>
            );
            return (
              <Box style={{
                position: 'absolute', zIndex: 7,
                ...(anchored ? { right: 14, bottom: timelineOpen ? 212 : 64 } : { left: mapToolPos.left, top: mapToolPos.top }),
                transition: anchored ? 'bottom 0.18s ease' : 'none',
                display: 'flex', alignItems: 'center', gap: 1,
                background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)',
                borderRadius: 12, padding: '2px 4px',
                border: '1px solid #e2e8f0', boxShadow: '0 6px 20px rgba(15,23,42,0.22)',
              }}>
                <Box onPointerDown={startToolDrag} title={pt ? 'Arrastar' : 'Drag'}
                  style={{ cursor: 'grab', touchAction: 'none', color: 'var(--live-muted2, #94a3b8)', fontSize: 15, letterSpacing: -2, padding: '0 4px', display: 'flex', alignItems: 'center', userSelect: 'none' }}>
                  ⠿
                </Box>
                {btn(handleRecenter, pt ? 'Centrar mapa' : 'Recenter', <CenterFocusStrongIcon style={{ fontSize: 18, color: 'var(--live-strong, #0f172a)' }} />)}
                {btn(() => setHelpOpen(v => !v), pt ? 'Legenda do mapa' : 'Map legend', <HelpOutlineIcon style={{ fontSize: 18, color: 'var(--live-strong, #0f172a)' }} />)}
                {btn(() => setTrafficOn(v => !v), pt ? 'Trânsito em tempo real' : 'Live traffic', <TrafficIcon style={{ fontSize: 18, color: trafficOn ? '#dc2626' : '#94a3b8' }} />)}
                {btn(() => setMapType(t => t === 'roadmap' ? 'hybrid' : t === 'hybrid' ? 'terrain' : 'roadmap'),
                  pt ? `Camada: ${mapType === 'roadmap' ? 'Mapa' : mapType === 'hybrid' ? 'Satélite' : 'Relevo'}` : `Layer: ${mapType}`,
                  <LayersIcon style={{ fontSize: 18, color: mapType === 'roadmap' ? '#0f172a' : '#2563eb' }} />)}
                {btn(() => setShowIncidents(s => !s), pt ? 'Acidentes / vias cortadas' : 'Traffic incidents', <ReportProblemOutlinedIcon style={{ fontSize: 18, color: showIncidents ? '#dc2626' : '#94a3b8' }} />)}
              </Box>
            );
          })()}

          {helpOpen && (
            <Box
              className={classes.helpPanel}
              style={{ bottom: timelineOpen ? 254 : 106, transition: 'bottom 0.18s ease' }}
            >
              <Box className={classes.helpTitle}>
                <span>{pt ? 'Legenda do mapa' : 'Map legend'}</span>
                <IconButton size="small" onClick={() => setHelpOpen(false)} style={{ padding: 2 }}>
                  <span style={{ fontSize: '1rem', color: 'var(--live-muted2, #94a3b8)' }}>×</span>
                </IconButton>
              </Box>
              <Box className={classes.helpRow}>
                <Box className={classes.helpSwatchSolid} />
                <Box>
                  <b>{pt ? 'Linha azul cheia' : 'Solid blue line'}</b><br />
                  {pt
                    ? 'Rota planeada — o trajecto que o motorista deveria seguir.'
                    : 'Planned route — the path the driver should follow.'}
                </Box>
              </Box>
              <Box className={classes.helpRow}>
                <Box className={classes.helpSwatchGreen} />
                <Box>
                  <b>{pt ? 'Tracejado verde' : 'Green dashes'}</b><br />
                  {pt
                    ? 'Trajecto real do veículo dentro do corredor planeado (≤300 m da rota).'
                    : 'Actual vehicle trail within the planned corridor (≤300 m of the route).'}
                </Box>
              </Box>
              <Box className={classes.helpRow}>
                <Box className={classes.helpSwatchRed} />
                <Box>
                  <b>{pt ? 'Tracejado vermelho' : 'Red dashes'}</b><br />
                  {pt
                    ? 'Trajecto real fora do corredor — desvio detectado.'
                    : 'Actual trail outside the corridor — deviation detected.'}
                </Box>
              </Box>
              <Box style={{ fontSize: '0.62rem', color: 'var(--live-muted, #64748b)', marginTop: 4, paddingTop: 6, borderTop: '1px dashed #e2e8f0' }}>
                {pt
                  ? 'Marcadores: 🟢 origem · 🔴 destino · números = ordem das paragens.'
                  : 'Markers: 🟢 origin · 🔴 destination · numbers = stop order.'}
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Cronologia da rota (faixa compacta) ────────────────
            Faixa full-width no fundo, mostrando origem → OTs → destino
            só com markers (✓ verde quando concluído) + label curta.
            A info detalhada por OT vive na sidebar — aqui é só
            visualização rápida do progresso e da posição do veículo. */}
        {(() => {
          const stops = sortedOTs.map((ot, i) => ({
            ot, index: i + 1, state: otVisualState(ot),
          }));
          const total = stops.length;
          const closed = stops.filter(s => s.state.kind === 'done').length;
          // "Onsite" para efeitos da barra inferior inclui as duas
          // sub-fases ao chegar: à espera (kind:onsite) e em execução
          // (kind:in_service). Sem isto, o veículo "desaparecia" da
          // marcação quando o motorista carregava em Iniciar Serviço.
          const onsiteIdx = stops.findIndex(s => s.state.kind === 'onsite' || s.state.kind === 'in_service');
          // Posição do veículo na rail (N+2 nós: origem, OTs, destino).
          // Espaçamento uniforme: cada nó representa 1/(N+1) do trajecto.
          // ── Movimento CONTÍNUO (não salta só nos marcos) ──────────────
          // Quando está numa paragem, encaixa no nó. Entre nós, interpola
          // pela distância GPS ao próximo ponto (haversine) → o carro desliza
          // ao vivo com cada nova posição recebida (a transição CSS suaviza).
          const originLL = Array.isArray(route?.origin?.coordinates) && route.origin.coordinates.length >= 2
            ? { lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] } : null;
          const destLL = Array.isArray(route?.destination?.coordinates) && route.destination.coordinates.length >= 2
            ? { lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] } : null;
          const stopLL = (i) => {
            const o = stops[i]?.ot;
            return (o && Number.isFinite(Number(o.deliveryLat)) && Number.isFinite(Number(o.deliveryLng)))
              ? { lat: Number(o.deliveryLat), lng: Number(o.deliveryLng) } : null;
          };
          const nodeLL = (idx) => idx <= 0 ? originLL : idx >= total + 1 ? destLL : stopLL(idx - 1);
          const gpsLL = (livePos && livePos.live !== false && Number.isFinite(livePos.lat) && Number.isFinite(livePos.lng))
            ? { lat: livePos.lat, lng: livePos.lng } : null;
          let progressUnits;
          if (onsiteIdx >= 0) {
            progressUnits = onsiteIdx + 1;               // parado numa paragem → encaixa no nó
          } else if (closed === total && total > 0) {
            // Todas concluídas: a conduzir do último nó até ao destino.
            const fromLL = nodeLL(total); const toLL = destLL;
            let frac = 1;
            if (gpsLL && fromLL && toLL) {
              const legTotal = haversineM(fromLL.lat, fromLL.lng, toLL.lat, toLL.lng);
              const legLeft = haversineM(gpsLL.lat, gpsLL.lng, toLL.lat, toLL.lng);
              frac = legTotal > 1 ? Math.max(0, Math.min(1, 1 - legLeft / legTotal)) : 1;
            }
            progressUnits = total + frac;
          } else {
            // A conduzir do último nó concluído (ou origem) até à próxima paragem.
            // SEM GPS fresco → fica no nó anterior (frac=0): rota não iniciada
            // ou sem sinal = 0% em vez de um "a meio" enganador.
            const fromIdx = closed; const toLL = stopLL(closed);
            const fromLL = nodeLL(fromIdx);
            let frac = 0;
            if (gpsLL && fromLL && toLL) {
              const legTotal = haversineM(fromLL.lat, fromLL.lng, toLL.lat, toLL.lng);
              const legLeft = haversineM(gpsLL.lat, gpsLL.lng, toLL.lat, toLL.lng);
              frac = legTotal > 1 ? Math.max(0, Math.min(1, 1 - legLeft / legTotal)) : 0;
            }
            progressUnits = fromIdx + frac;
          }
          const pct = Math.max(0, Math.min(1, progressUnits / (total + 1)));
          const pctLabel = `${Math.round(pct * 100)}%`;
          // A "linha verde preenchida" segue a mesma proporção mas
          // descontando as margens (left:18, right:18) da rail.
          const fillStyle = { width: `calc((100% - 36px) * ${pct})` };
          const vehicleStyle = { left: `calc(18px + (100% - 36px) * ${pct})` };
          // Igição actual — usada para colorir o ícone do veículo no
          // pino da timeline (amarelo = ralenti, vermelho = parado).
          const vehIdT = route.vehicleId || route.vehicle?.id;
          const vehObjT = vehIdT
            ? vehicles.find(x => Number(x.id) === Number(vehIdT))
            : null;
          const ignitionForTimeline = !!(vehObjT?.position?.attributes?.ignition);
          const headerPlate = livePos?.plate || route?.vehicle?.plate || '';
          return (
            <Box
              className={`${classes.timelinePanel}${timelineOpen ? '' : ' ' + classes.timelinePanelCollapsed}`}
            >
              <Box className={classes.timelineHeader}>
                <Box className={classes.timelineHeaderIcon}><TimelineIcon style={{ fontSize: 12 }} /></Box>
                <Typography className={classes.timelineTitle}>{pt ? 'Cronologia' : 'Timeline'}</Typography>
                {/* Percentagens — concluído vs por fazer, ambas visíveis */}
                <Box className={classes.timelinePctDone} title="Percentagem da rota já concluída">
                  ✓ {pctLabel}
                </Box>
                <Box className={classes.timelinePctLeft} title="Percentagem que ainda falta">
                  {100 - Math.round(pct * 100)}% por fazer
                </Box>
                <Typography className={classes.timelineSub}>{closed}/{total}</Typography>
                <Box style={{ flex: 1 }} />
                <IconButton size="small" onClick={() => setTimelineOpen(v => !v)} style={{ padding: 4 }}>
                  <ExpandMoreIcon style={{ fontSize: 18, color: 'var(--live-muted, #475569)', transform: timelineOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.15s' }} />
                </IconButton>
              </Box>
              {timelineOpen && (
                <Box className={classes.timelineBody}>
                  <Box className={classes.timelineRail}>
                    <Box className={classes.timelineRailLine} />
                    <Box className={classes.timelineRailLineFilled} style={fillStyle} />
                    {/* Pino do veículo — usa o MESMO ícone que o mapa
                        usa (EcooGPS PNG por tipo), rotacionado pelo rumo.
                        Mantém consistência visual: o operador vê o mesmo
                        carro deslocar-se no mapa e na timeline. */}
                    {(() => {
                      const vehStatus = livePos?.live === false ? 'gray'
                        : (livePos?.speed || 0) > 3 ? 'green'
                        : ignitionForTimeline ? 'yellow' : 'red';
                      const vehCat = vehicleIconCategory(liveVehicleType);
                      const vehIconUrl = getVehicleIconDataUrl(vehCat, vehStatus);
                      // Na barra de progresso o veículo é apenas um marcador
                      // da posição na rota (esquerda → direita = origem →
                      // destino). Os ícones têm a frente "para cima" no SVG;
                      // rotacionar 90° fixa a frente para a direita
                      // independentemente do rumo GPS, evitando ver o
                      // ícone deitado de lado ou de cabeça para baixo
                      // quando o motorista vai para norte/sul.
                      return (
                        <Box className={classes.timelineVehicle} style={vehicleStyle}>
                          <Tooltip title={headerPlate ? `Veículo · ${headerPlate}` : 'Veículo'} arrow>
                            <Box style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {vehIconUrl ? (
                                <img
                                  src={vehIconUrl}
                                  alt=""
                                  style={{ width: 26, height: 26, objectFit: 'contain', transform: 'rotate(90deg)' }}
                                />
                              ) : (
                                <Box style={{ width: 12, height: 12, borderRadius: '50%', background: vehStatus === 'green' ? '#16a34a' : vehStatus === 'yellow' ? '#eab308' : vehStatus === 'red' ? '#ef4444' : '#94a3b8' }} />
                              )}
                            </Box>
                          </Tooltip>
                        </Box>
                      );
                    })()}
                    {/* Origem */}
                    <Box className={classes.timelineStop}>
                      <Box className={classes.timelineMarker} style={{ background: '#4338ca' }}>
                        <HomeIcon style={{ fontSize: 14 }} />
                      </Box>
                      <Typography className={classes.timelineStopSub}>{pt ? 'Origem' : 'Origin'}</Typography>
                    </Box>
                    {/* OTs */}
                    {stops.map(({ ot, index, state }) => {
                      const isDone = state.kind === 'done';
                      const label = (ot.customRef || '').trim() || ot.orderNumber || `OT-${ot.id}`;
                      return (
                        <Box
                          key={ot.id}
                          className={classes.timelineStop}
                          onClick={() => setSelectedOTId(ot.id)}
                        >
                          <Box className={classes.timelineMarker} style={{ background: state.color }}>
                            {isDone ? <CheckCircleIcon style={{ fontSize: 14 }} /> : index}
                          </Box>
                          <Typography className={classes.timelineStopLabel}>{label}</Typography>
                        </Box>
                      );
                    })}
                    {/* Destino */}
                    <Box className={classes.timelineStop}>
                      <Box className={classes.timelineMarker} style={{
                        background: (route?.status === 'completed') ? '#16a34a' : '#0ea5e9',
                      }}>
                        {route?.status === 'completed'
                          ? <CheckCircleIcon style={{ fontSize: 14 }} />
                          : <FlagIcon style={{ fontSize: 14 }} />}
                      </Box>
                      <Typography className={classes.timelineStopSub}>
                        {route?.routeData?.phase === 'returning' && route?.status === 'in_progress'
                          ? (pt ? 'Em regresso' : 'Returning')
                          : (pt ? 'Destino' : 'Destination')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })()}

        {/* HUD de telemetria — canto inferior direito, debaixo do
            chip "GPS activo". Substitui o antigo `returningChip` (que
            só aparecia em regresso) por um card sempre visível com
            Status / Data / Velocidade / Distância percorrida / Total.
            Title bar muda de cor consoante o estado do veículo
            (verde a piscar no regresso). */}
        {livePos && (() => {
          const isReturning = route?.routeData?.phase === 'returning'
            && (route?.status === 'in_progress' || route?.dbStatus === 'in_progress');
          const vehId = route.vehicleId || route.vehicle?.id;
          const vehicleObj = vehId
            ? vehicles.find(x => Number(x.id) === Number(vehId))
            : null;
          const ignition = !!(vehicleObj?.position?.attributes?.ignition);
          const speedKmh = Math.max(0, Math.round(Number(livePos.speed) || 0));
          let state;
          if (!livePos.live) state = 'no_gps';
          else if (isReturning) state = 'returning';
          else if (deviation.active) state = 'off_route';
          else if (speedKmh > 3) state = 'moving';
          else if (ignition) state = 'idle';
          else state = 'stopped';
          // Em regresso: enriquece o label com o nome do destino e o
          // tempo decorrido, mantendo a cor verde piscante.
          let customLabel;
          if (isReturning) {
            const since = route?.routeData?.returningSince ? new Date(route.routeData.returningSince) : null;
            const min = since ? Math.max(0, Math.round((Date.now() - since.getTime()) / 60000)) : null;
            customLabel = min != null
              ? `Em regresso a casa · ${min} min`
              : 'Em regresso a casa';
          }
          return (
            <LiveTelemetryCard
              state={state}
              customLabel={customLabel}
              speedKmh={speedKmh}
              timestamp={livePos.fixTime || livePos.t || Date.now()}
              distanceTravelledM={distanceTravelledM}
              distanceTotalM={Number(route.totalDistanceM || route.routeData?.distance || 0)}
            />
          );
        })()}

        {/* Painel "veículo em trânsito" — HUD do canto inferior ESQUERDO
            com info rica: ícone real do veículo (mesmo que o mapa usa),
            matrícula, condutor (tacógrafo / RFID), morada actual,
            velocidade + semáforo verde/amarelo/vermelho, próxima
            paragem e progresso. Só aparece quando há posição. */}
        {livePos && (() => {
          const nextOT = sortedOTs.find(o => o.status !== 'delivered');
          const vehId = route.vehicleId || route.vehicle?.id;
          const vehicleObj = vehId
            ? vehicles.find(x => Number(x.id) === Number(vehId))
            : (livePos.plate ? vehicles.find(x => (x.plate || '').toUpperCase() === String(livePos.plate).toUpperCase()) : null);
          const ignition = !!(vehicleObj?.position?.attributes?.ignition);
          // Morada actual — Traccar reverse-geocode + fallback ao último
          // endereço conhecido na slice vehicles.
          const currentAddr = vehicleObj?.position?.address
            || vehicleObj?.address
            || null;
          // Condutor — io563 (tacógrafo) ou RFID via EcooGPS tacho_drivers.
          const driverName = vehicleObj?.driver
            || route.driver?.name
            || route?.routeDrivers?.[0]?.driver?.name
            || null;
          // Posição: por defeito sobe com a timeline; depois de arrastado,
          // fica na posição escolhida (fpPos).
          const defaultBottom = timelineOpen ? 224 : 130;
          const posStyle = fpPos
            ? { left: fpPos.left, bottom: fpPos.bottom, transition: 'none' }
            : { left: 12, bottom: defaultBottom, transition: 'bottom 0.18s ease' };
          // Recolhido → botão de carro para reabrir (mesmo padrão do painel de OTs).
          if (!firstPersonOpen) {
            return (
              <Tooltip title={pt ? 'Mostrar veículo em trânsito' : 'Show vehicle in transit'}>
                <IconButton
                  className={classes.fpReopen}
                  style={{ left: posStyle.left, bottom: posStyle.bottom }}
                  onClick={() => setFirstPersonOpen(true)}
                >
                  <DirectionsCarIcon style={{ fontSize: 20, color: '#1e3a8a' }} />
                </IconButton>
              </Tooltip>
            );
          }
          return (
            <Box className={classes.firstPersonPanel} style={posStyle}>
              {/* Barra de arrastar + fechar */}
              <Box
                className={classes.fpHandle}
                onMouseDown={(e) => {
                  if (e.button !== 0) return;
                  const panel = e.currentTarget.parentElement;
                  const startX = e.clientX, startY = e.clientY;
                  const startLeft = parseInt(panel.style.left) || 12;
                  const startBottom = parseInt(panel.style.bottom) || defaultBottom;
                  const onMove = (ev) => {
                    panel.style.left = (startLeft + (ev.clientX - startX)) + 'px';
                    panel.style.bottom = (startBottom - (ev.clientY - startY)) + 'px';
                    panel.style.transition = 'none';
                  };
                  const onUp = () => {
                    document.removeEventListener('mousemove', onMove);
                    document.removeEventListener('mouseup', onUp);
                    setFpPos({ left: parseInt(panel.style.left) || 12, bottom: parseInt(panel.style.bottom) || defaultBottom });
                  };
                  document.addEventListener('mousemove', onMove);
                  document.addEventListener('mouseup', onUp);
                }}
              >
                <span className={classes.fpHandleLabel}>
                  <Box className={classes.timelineHeaderIcon}><DirectionsCarIcon style={{ fontSize: 12 }} /></Box>
                  {pt ? 'Em trânsito' : 'In transit'}
                </span>
                <IconButton size="small" onClick={() => setFirstPersonOpen(false)} style={{ color: 'var(--live-muted, #64748b)', padding: 2 }}>
                  <CloseIcon style={{ fontSize: 15 }} />
                </IconButton>
              </Box>
              <Box style={{ flex: 1, minHeight: 0 }}>
                <DriverFirstPersonView
                  vehicle={vehicleObj || { type: liveVehicleType, plate: route?.vehicle?.plate || route?.vehiclePlate }}
                  plate={route?.vehicle?.plate || route?.vehiclePlate}
                  driverName={driverName}
                  heading={livePos.heading}
                  speed={livePos.speed}
                  ignition={ignition}
                  address={currentAddr}
                  isLive={!!livePos.live}
                  source={livePos.source}
                  delivered={stats.delivered}
                  total={stats.total}
                  nextStop={nextOT ? (otLabel(nextOT)) : null}
                  nextStopAddr={nextOT ? (nextOT.deliveryName || nextOT.deliveryAddress || '') : null}
                />
              </Box>
            </Box>
          );
        })()}
      </Box>

      {/* Confirmação estilizada para "Retirar OT da rota" — substitui o
          window.confirm. Mostra OT, rota, vehicle e driver mantidos. */}
      <ConfirmDialog
        open={!!removeOTConfirm}
        onClose={() => setRemoveOTConfirm(null)}
        onConfirm={performRemoveOT}
        severity="warning"
        title={pt ? 'Retirar ordem da rota' : 'Remove order from route'}
        confirmLabel={pt ? 'Retirar da rota' : 'Remove from route'}
        cancelLabel={pt ? 'Manter' : 'Keep'}
        message={(() => {
          if (!removeOTConfirm) return '';
          const otRef = removeOTConfirm.customRef || removeOTConfirm.orderNumber || `OT-${removeOTConfirm.id}`;
          const routeRef = route?.name || `Rota #${route?.id}`;
          const plate = route?.vehicle?.plate || route?.vehiclePlate || '—';
          const driver = route?.driver?.name || route?.routeData?.driverName || '—';
          return pt
            ? `${otRef} será retirada de "${routeRef}".\n\nO veículo ${plate} e o motorista ${driver} mantêm-se atribuídos à ordem — a OT volta para a lista "sem rota".\n\nA geometria da rota NÃO é recalculada automaticamente.`
            : `${otRef} will be removed from "${routeRef}".\n\nVehicle ${plate} and driver ${driver} stay assigned to the order — it goes back to the "no route" list.\n\nRoute geometry will NOT auto-recalculate.`;
        })()}
      />

      {/* Chat operador ↔ motorista da rota — reutiliza o diálogo já
          existente; a thread é a mesma da app móvel do motorista. */}
      {driverId && (
        <ChatDriverDialog
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          driverId={driverId}
          driverName={driverName}
          driverPhone={driverPhone}
          activeOT={chatActiveOT}
        />
      )}
      {/* Replay do percurso real — mesmo componente usado em Operações. */}
      {replayOpen && route?.id && (
        <RoutePlaybackDialog
          open={replayOpen}
          onClose={() => setReplayOpen(false)}
          routeId={route.id}
          routeName={route?.name}
        />
      )}
    </Box>
  );
}
